import React, { useState, useEffect, useMemo, useRef } from "react";
import InspirationTaskButton, { BulkInspirationModal } from "../components/InspirationTaskButton";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Upload, Package, Trash2, RefreshCw, ChevronDown, ChevronRight,
  Check, X, DollarSign, Calculator, Loader2, FileSpreadsheet,
  Copy, Info, Filter, Settings2, Layers, AlertTriangle, Plus, Minus, Sparkles, Star, Search, Send,
} from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { apiClient } from "../lib/api";
import { useShopVertical } from "../lib/useShopVertical";
import { authHeaders, getBackendUrl } from "../lib/http";
import { ImageLightbox } from "../components/ImageLightbox";
import { JEWELRY_TYPE_LABELS, guessJewelryType } from "../lib/jewelryType";
import { useJewelryTypes } from "../lib/useJewelryTypes";
import { ProductOptionsEditor } from "../components/catalog/ProductOptionsEditor";
import { ListingAnalysisPanel } from "../components/catalog/ListingAnalysisPanel";
import { SupplierNoticeModal } from "../components/catalog/SupplierNoticeModal";
import ShotFixModal from "../components/catalog/ShotFixModal";
import { useShotProducts, shotProductKey } from "../lib/useShotProducts";
import { productAttributes, DEFAULT_ETSY_SHIPPING_USD } from "../lib/jewelryListing";
import { descriptionPlainText, comboSizeLabel, etsyNetProfit, ETSY_FEE_MODEL, DEFAULT_DISCOUNT_PCT, discountedPrice } from "@erc/shared";
import type {
  SupplierCatalog, ProductCategory, PricingStrategy,
  GeneratedVariationResult, ComboVariationResult,
  ComboPricedVariant, UpdateCatalogGroupsPayload,
} from "@erc/shared";

// ---- Ozellik filtresi: urunun ad / eksen / AI analiz alanlarindan ----
const FEATURES: Array<[string, string, RegExp]> = [
  ["font", "Font", /\bfont|yaz[ıi] tipi|typeface/i],
  ["harf", "Harf", /\bharf|\binitial|\bletter/i],
  ["isim", "İsim", /\bisim|\bnames?\b/i],
  ["dogumTasi", "Doğum taşı", /do[gğ]um ta[sş]|birthstone/i],
  ["dogumCicegi", "Doğum çiçeği", /do[gğ]um [cç]i[cç]e|birth ?flower/i],
  ["tarih", "Tarih / koordinat", /\btarih|\bdate\b|koordinat|coordinate/i],
];
function productText(c: ProductCategory): string {
  const a = c.listingAnalysis;
  return [
    c.name,
    ...(c.source?.variationTypes ?? []).map((t) => `${t.name} ${t.values.join(" ")}`),
    ...(a?.fields ?? []).map((f) => `${f.labelTr} ${f.question ?? ""} ${(f.values ?? []).join(" ")} ${f.instructions ?? ""}`),
  ].join(" \n ");
}
const hasPersonalization = (c: ProductCategory) =>
  !!c.personalization?.enabled || !!c.listingAnalysis?.fields?.some((f) => f.role === "personalization");

/** Analizde ayni bilgiyi iki kez soran alanlar: listede/varyasyonda secilen bir sey
 *  (font, renk, zincir uzunlugu, dogum tasi AYI) metin sorusunun talimatinda tekrar
 *  isteniyor. Dogum tasi SAYISI + aylarin metne yazilmasi dogru kurgudur, sayilmaz. */
const CONFLICT_CONCEPTS: Array<[string, RegExp]> = [
  ["font", /\bfont|yaz[ıi] tipi|typeface/i],
  ["renk", /\bcolou?r\b|\bfinish\b|\brenk\b/i],
  ["zincir uzunluğu", /chain length|zincir uzunlu/i],
  ["doğum taşı", /birthstone|do[gğ]um ta[sş]/i],
];
const COUNT_RE = /say[ıi]s[ıi]|number of|\bcount\b|adet/i;
function analysisConflicts(c: ProductCategory): string[] {
  const a = c.listingAnalysis;
  if (!a?.fields) return [];
  const picked = a.pickedFields ? new Set(a.pickedFields) : null;
  const fs = a.fields.filter((f) => !picked || picked.has(f.key));
  const concepts = (t: string) => CONFLICT_CONCEPTS.filter(([, r]) => r.test(t)).map(([k]) => k)
    .filter((k) => !(k === "doğum taşı" && COUNT_RE.test(t)));
  const chosen = new Map<string, string>();
  for (const f of fs.filter((f) => f.kind === "list")) for (const k of concepts(`${f.labelTr} ${f.question ?? ""}`)) if (!chosen.has(k)) chosen.set(k, f.labelTr);
  const out: string[] = [];
  for (const f of fs.filter((f) => f.kind === "text")) {
    for (const k of concepts(`${f.question ?? ""} ${f.instructions ?? ""}`)) {
      if (chosen.has(k)) out.push(`${k}: “${chosen.get(k)}” seçiliyor, “${f.labelTr}” metninde yeniden isteniyor`);
    }
  }
  return [...new Set(out)];
}

const VERTICAL_LABEL: Record<string, string> = {
  apparel: "Tekstil", giftware: "Hediyelik", jewelry: "Takı",
};

type PricingMode = "margin_percent" | "fixed_profit" | "target_price" | "catalog_fixed";

/** Kart kapagi: uretilen fotograf (kapak) > referans fotograf > ilk tedarikci fotografi */
function coverOf(cat: ProductCategory, shotCover?: string | null): string | null {
  const abs = (u: string) => (u.startsWith("http") || u.startsWith("data:") ? u : `${getBackendUrl()}${u}`);
  if (shotCover) return abs(shotCover);
  if (cat.referenceImageUrl) return abs(cat.referenceImageUrl);
  return cat.source?.images?.[0] ?? null;
}

// ============================================
// Main Page
// ============================================

export default function SupplierCatalogsPage() {
  const queryClient = useQueryClient();
  const [showImport, setShowImport] = useState(false);
  const [showNotice, setShowNotice] = useState(false);
  // Eklentideki "Katalogda goster": ?catalog=<id>&product=<urun id> — katalog
  // acilir ve urunun detayi kendiliginden gosterilir
  const [searchParams] = useSearchParams();
  const linkCatalogId = searchParams.get("catalog");
  const linkProductId = searchParams.get("product");
  const [selectedCatalogId, setSelectedCatalogId] = useState<string | null>(linkCatalogId);
  // Katalog sayfasi acilinca urunler GORUNSUN: her girisde katalogu acmak
  // gereksiz bir tiklamaydi. Ilk katalog kendiliginden acilir.
  const autoOpenedRef = useRef(false);
  const [showVariationBuilder, setShowVariationBuilder] = useState(false);
  const [builderCategoryId, setBuilderCategoryId] = useState<string | null>(null);
  const [showComboBuilder, setShowComboBuilder] = useState(false);
  // Fiyat modali: tek kategori ya da (toplu duzenlemede) birden fazla
  const [priceCategoryIds, setPriceCategoryIds] = useState<string[] | null>(null);

  // Aktif magazanin dikeyi disindaki kataloglar varsayilan olarak gizlenir:
  // taki magazasinda tekstil (tisort) kataloglarinin islevi yok. Gizlenenler
  // sayilir ve tek tikla acilabilir — "katalogum kayboldu" hissi olmasin.
  const { vertical } = useShopVertical();
  const [showAllVerticals, setShowAllVerticals] = useState(false);
  const { data: allCatalogs, isLoading } = useQuery({
    queryKey: ["supplier-catalogs"],
    queryFn: () => apiClient.listSupplierCatalogs(),
  });
  const catalogs = showAllVerticals
    ? allCatalogs
    : allCatalogs?.filter((c) => (c.vertical ?? "apparel") === vertical);
  const hiddenByVertical = (allCatalogs?.length ?? 0) - (catalogs?.length ?? 0);

  // Baglantidaki katalog baska dikeydeyse (gizli) gorunur yapilir
  useEffect(() => {
    if (linkCatalogId && allCatalogs?.some((c) => c.id === linkCatalogId) && !catalogs?.some((c) => c.id === linkCatalogId)) {
      setShowAllVerticals(true);
    }
  }, [linkCatalogId, allCatalogs, catalogs]);

  // Ilk yuklemede ilk katalogu ac (kullanici kapatirsa tekrar acilmaz)
  useEffect(() => {
    if (autoOpenedRef.current) return;
    const first = catalogs?.[0];
    if (!first) return;
    autoOpenedRef.current = true;
    setSelectedCatalogId((cur) => cur ?? first.id);
  }, [catalogs]);


  const selectedCatalog = catalogs?.find((c) => c.id === selectedCatalogId);

  return (
    <div className="mx-auto max-w-7xl px-4 py-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tedarikçi Katalogları</h1>
          <p className="mt-1 text-sm text-gray-500">
            Tedarikçi ürün listelerini Excel'den içeri aktarın, varyasyon şablonları oluşturun
          </p>
        </div>
        <div className="flex items-center gap-2">
        <button onClick={() => setShowNotice(true)}
          title="Tedarikçi duyurusunu yapıştır: kaldırılan ürünleri Etsy'de pasife al, fiyatı değişenleri işaretle"
          className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-800 hover:bg-amber-100">
          <AlertTriangle className="h-4 w-4" />
          Tedarikçi duyurusu
        </button>
        {/* Bos katalog: eklentiden aktarim yapmadan once hedef katalogu
            olusturmak icin. Dikeyi burada secilir; eklenti listede gorur. */}
        <button
          onClick={async () => {
            const name = prompt("Katalog adı:", vertical === "jewelry" ? "Takı Katalog" : "Yeni Katalog");
            if (!name?.trim()) return;
            try {
              await apiClient.createSupplierCatalog({
                name: name.trim(), categories: [], vertical,
              } as never);
              queryClient.invalidateQueries({ queryKey: ["supplier-catalogs"] });
            } catch (e) {
              alert("Katalog oluşturulamadı: " + (e instanceof Error ? e.message : String(e)));
            }
          }}
          className="flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-100"
        >
          <Plus className="h-4 w-4" />
          Katalog Oluştur
        </button>
        <button
          onClick={() => setShowImport(true)}
          className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
        >
          <Upload className="h-4 w-4" />
          Excel Yükle
        </button>
        </div>
      </div>

      {showNotice && (
        <SupplierNoticeModal onClose={() => setShowNotice(false)}
          onDone={() => queryClient.invalidateQueries({ queryKey: ["supplier-catalogs"] })} />
      )}

      {/* Import Modal */}
      {showImport && (
        <ImportModal
          onClose={() => setShowImport(false)}
          onSuccess={() => {
            setShowImport(false);
            queryClient.invalidateQueries({ queryKey: ["supplier-catalogs"] });
          }}
        />
      )}

      {/* Variation Builder Modal */}
      {showVariationBuilder && selectedCatalog && builderCategoryId && (
        <VariationBuilderModal
          catalog={selectedCatalog}
          categoryId={builderCategoryId}
          onClose={() => {
            setShowVariationBuilder(false);
            setBuilderCategoryId(null);
          }}
        />
      )}

      {/* Combo Builder Modal */}
      {showComboBuilder && selectedCatalog && (
        <ComboVariationBuilderModal
          catalog={selectedCatalog}
          onClose={() => setShowComboBuilder(false)}
        />
      )}

      {priceCategoryIds && selectedCatalog && (
        <CategoryPricesModal
          catalog={selectedCatalog}
          categoryIds={priceCategoryIds}
          onClose={() => setPriceCategoryIds(null)}
          onSaved={() => queryClient.invalidateQueries({ queryKey: ["supplier-catalogs"] })}
        />
      )}

      {/* Dikey suzgeci bildirimi */}
      {hiddenByVertical > 0 && (
        <button
          onClick={() => setShowAllVerticals((v) => !v)}
          className="mb-3 flex w-full items-center gap-2 rounded-lg border border-dashed border-gray-300 px-3 py-2 text-xs text-gray-500 hover:border-indigo-400 hover:text-indigo-600"
        >
          <Filter className="h-3.5 w-3.5" />
          {showAllVerticals
            ? `Yalnızca bu mağazanın (${VERTICAL_LABEL[vertical] ?? vertical}) kataloglarını göster`
            : `${hiddenByVertical} katalog başka dikeyde gizli — hepsini göster`}
        </button>
      )}

      {/* Catalog List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      ) : !catalogs || catalogs.length === 0 ? (
        <EmptyState onImport={() => setShowImport(true)} />
      ) : (
        <div className="space-y-4">
          {catalogs.map((catalog) => (
            <CatalogCard
              key={catalog.id}
              catalog={catalog}
              isSelected={selectedCatalogId === catalog.id}
              openProductId={catalog.id === linkCatalogId ? linkProductId : null}
              onSelect={() =>
                setSelectedCatalogId(
                  selectedCatalogId === catalog.id ? null : catalog.id
                )
              }
              onReimport={(file) => {
                apiClient.reimportSupplierCatalogExcel(catalog.id, file).then(() => {
                  queryClient.invalidateQueries({ queryKey: ["supplier-catalogs"] });
                });
              }}
              onBuildVariations={(categoryId) => {
                setSelectedCatalogId(catalog.id);
                setBuilderCategoryId(categoryId);
                setShowVariationBuilder(true);
              }}
              onComboBuilder={() => {
                setSelectedCatalogId(catalog.id);
                setShowComboBuilder(true);
              }}
              onEditPrices={(categoryId) => {
                setSelectedCatalogId(catalog.id);
                setPriceCategoryIds([categoryId]);
              }}
              onVerticalChange={(v) => {
                apiClient.updateSupplierCatalog(catalog.id, { vertical: v } as never)
                  .then(() => queryClient.invalidateQueries({ queryKey: ["supplier-catalogs"] }))
                  .catch(() => { /* sunucu reddederse liste degismez */ });
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================
// Kategori Fiyatlari (SABIT satis fiyati)
// ============================================
// Fiyatin tek dogruluk kaynagi katalogtur: burada girilen fiyat combo/varyasyon
// uretiminde hesaplama yapilmadan aynen kullanilir ve kaydedilince ayni kataloga
// bagli sablonlara sunucuda yayilir.
function CategoryPricesModal({
  catalog,
  categoryIds,
  onClose,
  onSaved,
}: {
  catalog: SupplierCatalog;
  categoryIds: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const allCats = (catalog.categories ?? []) as ProductCategory[];
  const [selectedIds, setSelectedIds] = useState<string[]>(categoryIds);
  const cats = allCats.filter((c) => selectedIds.includes(c.id));

  // categoryId -> (size -> girilen metin). Bos metin = sabit fiyat yok.
  const [draft, setDraft] = useState<Record<string, Record<string, string>>>(() => {
    const d: Record<string, Record<string, string>> = {};
    for (const c of allCats) {
      d[c.id] = {};
      for (const s of c.sizeOptions) {
        d[c.id][s.size] = typeof s.retailPrice === "number" && s.retailPrice > 0 ? String(s.retailPrice) : "";
      }
    }
    return d;
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  // Toplu araclar
  const [bulkValue, setBulkValue] = useState("");
  const [bulkMargin, setBulkMargin] = useState("40");
  const ETSY_FEE = 6.5;

  const setPrice = (catId: string, size: string, val: string) =>
    setDraft((prev) => ({ ...prev, [catId]: { ...prev[catId], [size]: val } }));

  const num = (v: string): number | null => {
    const n = Number(String(v).replace(",", "."));
    return Number.isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : null;
  };

  /** Secili TUM kategorilerin TUM bedenlerine uygula */
  function applyAll(fn: (s: { size: string; cost: number; shippingCost?: number }, catId: string) => string) {
    setDraft((prev) => {
      const next = { ...prev };
      for (const c of cats) {
        next[c.id] = { ...next[c.id] };
        for (const s of c.sizeOptions) next[c.id][s.size] = fn(s, c.id);
      }
      return next;
    });
  }

  const filledCount = cats.reduce(
    (sum, c) => sum + c.sizeOptions.filter((s) => num(draft[c.id]?.[s.size] ?? "") !== null).length, 0,
  );
  const totalSizes = cats.reduce((sum, c) => sum + c.sizeOptions.length, 0);

  async function save() {
    setSaving(true); setMsg(null);
    try {
      const updates = cats.map((c) => ({
        categoryId: c.id,
        prices: c.sizeOptions.map((s) => ({ size: s.size, retailPrice: num(draft[c.id]?.[s.size] ?? "") })),
      }));
      const r = await apiClient.updateSupplierCatalogPrices(catalog.id, updates);
      onSaved();
      setMsg(
        r.syncedTemplates > 0
          ? `Kaydedildi. ${r.syncedTemplates} şablon güncellendi: ${r.syncedTemplateNames.join(", ")}`
          : "Kaydedildi. (Bu kategorilere bağlı combo şablonu yok)",
      );
    } catch (e) {
      setMsg("Kaydedilemedi: " + (e instanceof Error ? e.message : String(e)));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 pt-10" onClick={onClose}>
      <div className="w-full max-w-4xl rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              <DollarSign className="mr-1.5 inline h-5 w-5 text-emerald-600" />
              Sabit Satış Fiyatları
            </h2>
            <p className="mt-0.5 text-xs text-gray-500">
              Girilen fiyat combo/varyasyon üretiminde hesaplanmadan aynen kullanılır.
              Kaydedince bu kataloğa bağlı combo şablonlarının beden fiyatları da güncellenir.
            </p>
          </div>
          <button onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100"><X className="h-5 w-5" /></button>
        </div>

        {/* Kategori secimi — toplu duzenleme icin birden fazla secilebilir */}
        <div className="mb-3 rounded-lg border border-gray-200 bg-gray-50 p-2">
          <p className="mb-1.5 text-[11px] font-semibold text-gray-600">Düzenlenecek ürünler ({cats.length})</p>
          <div className="flex max-h-24 flex-wrap gap-1 overflow-y-auto">
            {allCats.map((c) => {
              const on = selectedIds.includes(c.id);
              return (
                <button
                  key={c.id}
                  onClick={() => setSelectedIds((prev) => (on ? prev.filter((x) => x !== c.id) : [...prev, c.id]))}
                  className={`rounded border px-2 py-0.5 text-[11px] ${
                    on ? "border-emerald-400 bg-emerald-50 text-emerald-700" : "border-gray-200 text-gray-500 hover:border-gray-300"
                  }`}
                >
                  {on && <Check className="mr-0.5 inline h-2.5 w-2.5" />}{c.name}
                </button>
              );
            })}
          </div>
        </div>

        {/* Toplu araclar */}
        <div className="mb-3 flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
          <div>
            <label className="mb-0.5 block text-[11px] font-medium text-gray-600">Tümüne aynı fiyat ($)</label>
            <div className="flex gap-1">
              <input
                value={bulkValue}
                onChange={(e) => setBulkValue(e.target.value)}
                placeholder="29.99"
                className="w-24 rounded border border-gray-200 px-2 py-1 text-sm"
              />
              <button
                onClick={() => { const v = num(bulkValue); if (v !== null) applyAll(() => String(v)); }}
                className="rounded bg-gray-900 px-2.5 py-1 text-xs text-white hover:bg-gray-800"
              >
                Uygula
              </button>
            </div>
          </div>

          <div>
            <label className="mb-0.5 block text-[11px] font-medium text-gray-600">Maliyet + kâr marjı (%)</label>
            <div className="flex gap-1">
              <input
                value={bulkMargin}
                onChange={(e) => setBulkMargin(e.target.value)}
                className="w-20 rounded border border-gray-200 px-2 py-1 text-sm"
              />
              <button
                title="Her bedenin maliyeti + kargosu üzerinden hedef marjı tutturan fiyatı yazar"
                onClick={() => {
                  const m = Number(String(bulkMargin).replace(",", ".")) / 100;
                  const divisor = 1 - m - ETSY_FEE / 100;
                  if (!(divisor > 0)) return;
                  applyAll((s) => {
                    const total = s.cost + (s.shippingCost ?? 0);
                    return String(Math.ceil((total / divisor) * 100 - 1) / 100);
                  });
                }}
                className="rounded bg-gray-900 px-2.5 py-1 text-xs text-white hover:bg-gray-800"
              >
                Doldur
              </button>
            </div>
          </div>

          <div className="flex gap-1">
            <button
              title="Girili fiyatları %10 artır"
              onClick={() => applyAll((s, catId) => {
                const cur = num(draft[catId]?.[s.size] ?? "");
                return cur === null ? "" : String(Math.round(cur * 1.1 * 100) / 100);
              })}
              className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-100"
            >
              +%10
            </button>
            <button
              title="Girili fiyatları .99 ile biten değere yuvarla"
              onClick={() => applyAll((s, catId) => {
                const cur = num(draft[catId]?.[s.size] ?? "");
                return cur === null ? "" : String(Math.round((Math.ceil(cur) - 0.01) * 100) / 100);
              })}
              className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-100"
            >
              .99
            </button>
            <button
              title="Sabit fiyatları kaldır — o bedenler yine hesaplanan fiyatı kullanır"
              onClick={() => applyAll(() => "")}
              className="rounded border border-red-200 px-2 py-1 text-xs text-red-600 hover:bg-red-50"
            >
              Temizle
            </button>
          </div>

          <span className="ml-auto text-xs text-gray-500">{filledCount}/{totalSizes} bedende sabit fiyat</span>
        </div>

        {/* Beden tablolari */}
        <div className="max-h-[45vh] space-y-4 overflow-y-auto">
          {cats.map((cat) => (
            <div key={cat.id} className="rounded-lg border border-gray-200">
              <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2">
                <p className="text-sm font-semibold text-gray-800">{cat.name} <span className="font-normal text-gray-400">{cat.brand}</span></p>
                <span className="text-[11px] text-gray-400">{cat.sizeOptions.length} beden</span>
              </div>
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-gray-500">
                  <tr>
                    <th className="px-3 py-1.5 text-left font-medium">Beden</th>
                    <th className="px-3 py-1.5 text-right font-medium">Maliyet</th>
                    <th className="px-3 py-1.5 text-right font-medium">Kargo</th>
                    <th className="px-3 py-1.5 text-right font-medium">Sabit Satış $</th>
                    <th className="px-3 py-1.5 text-right font-medium">Kâr</th>
                    <th className="px-3 py-1.5 text-right font-medium">Marj</th>
                  </tr>
                </thead>
                <tbody>
                  {cat.sizeOptions.map((s) => {
                    const price = num(draft[cat.id]?.[s.size] ?? "");
                    const ship = s.shippingCost ?? 0;
                    // Kar = fiyat - maliyet - kargo - Etsy komisyonu - listeleme ucreti
                    const profit = price === null ? null : price - s.cost - ship - price * (ETSY_FEE / 100) - 0.2;
                    const margin = price && profit !== null ? (profit / price) * 100 : null;
                    return (
                      <tr key={s.size} className="border-t border-gray-50">
                        <td className="px-3 py-1.5 text-gray-700">{s.size}</td>
                        <td className="px-3 py-1.5 text-right text-gray-500">${s.cost.toFixed(2)}</td>
                        <td className="px-3 py-1.5 text-right text-gray-400">{ship ? `$${ship.toFixed(2)}` : "-"}</td>
                        <td className="px-3 py-1.5 text-right">
                          <input
                            value={draft[cat.id]?.[s.size] ?? ""}
                            onChange={(e) => setPrice(cat.id, s.size, e.target.value)}
                            placeholder="hesaplansın"
                            className={`w-24 rounded border px-2 py-0.5 text-right text-xs ${
                              price === null ? "border-gray-200 text-gray-400" : "border-emerald-300 bg-emerald-50 text-emerald-800"
                            }`}
                          />
                        </td>
                        <td className={`px-3 py-1.5 text-right ${profit !== null && profit < 0 ? "text-red-600" : "text-gray-600"}`}>
                          {profit === null ? "-" : `$${profit.toFixed(2)}`}
                        </td>
                        <td className={`px-3 py-1.5 text-right ${margin !== null && margin < 20 ? "text-red-600" : "text-gray-500"}`}>
                          {margin === null ? "-" : `${margin.toFixed(0)}%`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </div>

        {msg && <p className="mt-3 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-700">{msg}</p>}

        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50">
            Kapat
          </button>
          <button
            onClick={save}
            disabled={saving || cats.length === 0}
            className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            Kaydet ve şablonlara uygula
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Empty State
// ============================================

// Katalog urun durum filtreleri: [anahtar, etiket, aciklama]
type StateKey = "noInspiration" | "noPhoto" | "noDraft" | "ready" | "draftOnly" | "onEtsy";
const STATE_FILTERS: Array<[StateKey | "", string, string]> = [
  ["", "Tümü", "Tüm ürünler"],
  ["noInspiration", "İlham listesi yok", "Ürüne ilham listesi (benzer satan Etsy listesi) eklenmemiş"],
  ["noPhoto", "Fotoğraf yok", "Studio'da ürün fotoğrafı üretilmemiş"],
  ["noDraft", "Taslağı yok", "Bu üründen henüz liste taslağı açılmamış"],
  ["ready", "Liste yapılabilir", "İlham listesi ve fotoğrafı hazır, henüz taslağı yok"],
  ["draftOnly", "Taslakta", "Taslağı var ama Etsy'de yok"],
  ["onEtsy", "Etsy'de", "Etsy'ye gönderilmiş"],
];

function EmptyState({ onImport }: { onImport: () => void }) {
  return (
    <div className="rounded-xl border-2 border-dashed border-gray-200 p-12 text-center">
      <FileSpreadsheet className="mx-auto h-12 w-12 text-gray-300" />
      <h3 className="mt-4 text-lg font-medium text-gray-900">Henüz katalog yok</h3>
      <p className="mt-2 text-sm text-gray-500">
        Tedarikçi ürün listenizi Excel dosyası olarak yükleyin.<br />
        Bedenler, renkler, stok durumu ve maliyet fiyatları otomatik çıkarılacak.
      </p>
      <button
        onClick={onImport}
        className="mt-4 inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
      >
        <Upload className="h-4 w-4" />
        Excel Yükle
      </button>
    </div>
  );
}

// ============================================
// Catalog Card
// ============================================

function CatalogCard({
  catalog,
  isSelected,
  onSelect,
  onReimport,
  onBuildVariations,
  onComboBuilder,
  onEditPrices,
  onVerticalChange,
  openProductId,
}: {
  /** Baglantiyla gelindiyse detayi kendiliginden acilacak urun */
  openProductId?: string | null;
  catalog: SupplierCatalog;
  isSelected: boolean;
  onSelect: () => void;
  onReimport: (file: File) => void;
  onBuildVariations: (categoryId: string) => void;
  onComboBuilder: () => void;
  onEditPrices: (categoryId: string) => void;
  onVerticalChange: (vertical: string) => void;
}) {
  const allCategories = (catalog.categories ?? []) as ProductCategory[];
  // Taki hattinda uretim yok: urun tedarikciden hazir gelir, dogrudan
  // Trendyol listesine cevrilir. Tekstilde varyasyon uretimi devam eder.
  // Builder her iki pazaryerinde de calisiyor: pazaryeri farki (kategori vs
  // taksonomi, TRY vs USD, etiket) sayfanin kendi icinde cozuluyor.
  const isJewelry = (catalog.vertical ?? "apparel") === "jewelry";

  // Pazar filtresi: urun hangi ulkeler icin uygun (eklentide isaretlenir).
  // "" = tumu. Pazar bilgisi girilmemis urunler her filtrede gorunur ki
  // eski aktarimlar aniden kaybolmasin.
  const [marketFilter, setMarketFilter] = useState<"" | "US" | "TR">("");
  const [search, setSearch] = useState("");
  // Kategori (grup) filtresi ve kapali gruplar; kapali gruplar tarayicida hatirlanir
  const [groupFilter, setGroupFilter] = useState("");
  const collapseKey = `erc_catalog_collapsed_${catalog.id}`;
  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem(collapseKey) ?? "[]") as string[]); } catch { return new Set(); }
  });
  const toggleCollapsed = (g: string) => setCollapsed((prev) => {
    const next = new Set(prev);
    if (next.has(g)) next.delete(g); else next.add(g);
    try { localStorage.setItem(collapseKey, JSON.stringify([...next])); } catch { /* yok say */ }
    return next;
  });
  // Kapak gorseli: takida uretilen fotograflardan (tek istekle tum urunler)
  const { data: shotData } = useShotProducts(isJewelry);
  const shotByKey = useMemo(() => new Map((shotData?.items ?? []).map((i) => [i.key, i])), [shotData]);
  // Liste durumu: liste yapmak icin eksikleri bulmaya yarar
  const [stateFilter, setStateFilter] = useState<StateKey | "">("");
  // Ozellik filtresi (coklu secim, hepsi saglanmali)
  const [featureFilter, setFeatureFilter] = useState<string[]>([]);
  const matchFeature = (c: ProductCategory, k: string) => {
    if (k === "none") return !hasPersonalization(c);
    if (k === "noAnalysis") return !c.listingAnalysis?.fields;
    if (k === "conflict") return analysisConflicts(c).length > 0;
    const re = FEATURES.find(([key]) => key === k)?.[2];
    return !!re && re.test(productText(c));
  };
  const stateOf = (c: ProductCategory) => {
    const shot = shotByKey.get(shotProductKey(catalog.id, c.id));
    return {
      noInspiration: !(c.inspirations?.length),
      noPhoto: isJewelry && !(shot?.shotCount),
      hasDraft: (shot?.drafts.length ?? 0) > 0,
      onEtsy: !!shot?.drafts.some((d) => d.etsyListingId),
    };
  };
  const matchState = (c: ProductCategory, k: StateKey | "") => {
    if (!k) return true;
    const st = stateOf(c);
    if (k === "noInspiration") return st.noInspiration;
    if (k === "noPhoto") return st.noPhoto;
    if (k === "noDraft") return !st.hasDraft;
    // Liste yapilabilir: ilham + (takida) fotograf hazir, henuz taslak yok
    if (k === "ready") return !st.noInspiration && !st.noPhoto && !st.hasDraft;
    if (k === "draftOnly") return st.hasDraft && !st.onEtsy;
    return st.onEtsy;
  };
  // Ayrinti modali acik olan urun — dar kartta okunmayan her sey orada
  const [detailProductId, setDetailProductId] = useState<string | null>(null);
  const [bulkInspiration, setBulkInspiration] = useState(false);
  // Baglantidaki urun bir kez acilir (kapatilinca tekrar acilmaz)
  const openedLinkRef = useRef(false);
  useEffect(() => {
    if (openedLinkRef.current || !openProductId) return;
    if (!allCategories.some((c) => c.id === openProductId)) return;
    openedLinkRef.current = true;
    setDetailProductId(openProductId);
  }, [openProductId, allCategories]);
  // Tam ekran gorsel: hem tedarikci fotograflari hem uretilen gorseller ayni
  // onizleyiciyi kullanir, oklarla seri gezilir.
  const [lightbox, setLightbox] = useState<{
    urls: string[];
    index: number;
    /** Uretilen gorseller tam ekranda da silinebilir (tedarikci fotograflari degil) */
    onDelete?: (i: number) => void;
  } | null>(null);
  const categories = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allCategories.filter((c) => {
      if (q && !`${c.name} ${c.source?.sku ?? ""}`.toLowerCase().includes(q)) return false;
      if (!matchState(c, stateFilter)) return false;
      if (!featureFilter.every((k) => matchFeature(c, k))) return false;
      if (!marketFilter) return true;
      const m = c.source?.markets;
      if (!m || m.length === 0) return true;
      return m.includes(marketFilter);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allCategories, marketFilter, search, stateFilter, shotByKey, featureFilter]);

  const marketCounts = useMemo(() => {
    let us = 0, tr = 0, both = 0, none = 0;
    for (const c of allCategories) {
      const m = c.source?.markets ?? [];
      if (m.length === 0) none++;
      else if (m.includes("US") && m.includes("TR")) both++;
      else if (m.includes("US")) us++;
      else if (m.includes("TR")) tr++;
    }
    return { us, tr, both, none };
  }, [allCategories]);
  const totalColors = new Set(categories.flatMap((c: ProductCategory) => c.colorAvailability.map((ca: { color: string }) => ca.color))).size;
  const totalSizes = new Set(categories.flatMap((c: ProductCategory) => c.sizeOptions.map((s: { size: string }) => s.size))).size;

  const queryClient = useQueryClient();
  const UNGROUPED = "Gruplanmamis";

  // Katalogdaki gruplar: metadata'da tanimlilar + fiilen kullanilanlar
  const groups = useMemo(() => {
    const set = new Set<string>();
    for (const g of (catalog.metadata?.groups ?? [])) if (g?.trim()) set.add(g.trim());
    for (const c of categories) if (c.group?.trim()) set.add(c.group.trim());
    return [...set].sort((a, b) => a.localeCompare(b, "tr"));
  }, [catalog.metadata, categories]);

  // Gruplara gore bolunmus kartlar — bos gruplar da gorunur
  const grouped = useMemo(() => {
    const map = new Map<string, ProductCategory[]>();
    for (const g of groups) map.set(g, []);
    for (const c of categories) {
      const key = c.group?.trim() || UNGROUPED;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(c);
    }
    // Gruplanmamis her zaman en sonda
    const entries = [...map.entries()].filter(([k]) => k !== UNGROUPED);
    const un = map.get(UNGROUPED);
    if (un?.length) entries.push([UNGROUPED, un]);
    return groupFilter ? entries.filter(([k]) => k === groupFilter) : entries;
  }, [groups, categories, groupFilter]);

  const groupMutation = useMutation({
    mutationFn: (payload: UpdateCatalogGroupsPayload) =>
      apiClient.updateSupplierCatalogGroups(catalog.id, payload),
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: ["supplier-catalogs"] }); },
  });

  // Tek urunu katalogdan siler (katalogun tamamini silen dugme kaldirildi: fazla tehlikeli)
  async function deleteProduct(cat: ProductCategory) {
    if (!confirm(`"${cat.name}"${cat.source?.sku ? ` (${cat.source.sku})` : ""} katalogdan silinsin mi?\n\nTaslaklar, Etsy ilanları ve üretilmiş görseller silinmez.`)) return;
    const r = await fetch(`${getBackendUrl()}/api/supplier-catalogs/${catalog.id}/categories/${cat.id}`, { method: "DELETE", headers: authHeaders() });
    if (!r.ok) { alert(((await r.json().catch(() => ({}))) as { error?: string }).error || "Ürün silinemedi"); return; }
    void queryClient.invalidateQueries({ queryKey: ["supplier-catalogs"] });
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
      {/* Header */}
      <div
        className="flex cursor-pointer items-center justify-between px-5 py-4"
        onClick={onSelect}
      >
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-50">
            <Package className="h-5 w-5 text-indigo-600" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-gray-900">{catalog.name}</h3>
              {/* Katalogun dikeyi: hangi magaza turunde listelenecegini belirler */}
              <select
                value={catalog.vertical ?? "apparel"}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => { e.stopPropagation(); onVerticalChange(e.target.value); }}
                title="Bu katalog hangi mağaza türünde görünsün"
                className="rounded border border-gray-200 bg-white px-1.5 py-0.5 text-[10px] text-gray-600"
              >
                {Object.entries(VERTICAL_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-3 text-xs text-gray-500">
              {catalog.supplier && <span>{catalog.supplier}</span>}
              {isJewelry ? (
                <span>{allCategories.length} ürün</span>
              ) : (<>
                <span>{categories.length} kategori</span>
                <span>{totalColors} renk</span>
                <span>{totalSizes} beden</span>
                <span>v{catalog.version}</span>
              </>)}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex cursor-pointer items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50">
            <RefreshCw className="h-3 w-3" />
            Güncelle
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onReimport(f);
                e.target.value = "";
              }}
            />
          </label>
          {isSelected ? (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronRight className="h-5 w-5 text-gray-400" />
          )}
        </div>
      </div>

      {/* Expanded: Category list */}
      {isSelected && (
        <div className="border-t border-gray-100 px-5 py-4">
          {/* Combo varyasyon — en ustte, en cok kullanilan islem */}
          {!isJewelry && categories.length >= 2 && (
            <div className="mb-4">
              <button
                onClick={onComboBuilder}
                className="flex items-center gap-2 rounded-lg border-2 border-dashed border-indigo-300 bg-indigo-50/50 px-4 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-100"
              >
                <Layers className="h-4 w-4" />
                Combo Varyasyon (Çoklu Kategori)
              </button>
              <p className="mt-1 text-xs text-gray-400">
                Birden fazla kategoriyi tek listede birleştirin (örn: Shirt + Sweatshirt)
              </p>
            </div>
          )}

          {/* Grup yonetimi */}
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                placeholder="Ürün adı ya da SKU ara…"
                className="w-56 rounded-lg border border-gray-200 py-1.5 pl-8 pr-2 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-200"
              />
            </div>
            <span className="mx-1 text-gray-300">|</span>
            <span className="text-xs font-medium text-gray-500">Durum:</span>
            {STATE_FILTERS.filter(([k]) => isJewelry || k !== "noPhoto").map(([key, label, hint]) => {
              const n = key ? allCategories.filter((c) => matchState(c, key)).length : allCategories.length;
              return (
                <button key={key || "all"} title={hint}
                  onClick={(e) => { e.stopPropagation(); setStateFilter(key); }}
                  className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors ${
                    stateFilter === key ? "bg-indigo-100 text-indigo-700 ring-1 ring-indigo-300" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}>
                  {label} ({n})
                </button>
              );
            })}
            {isJewelry && (<>
              <span className="mx-1 text-gray-300">|</span>
              <span className="text-xs font-medium text-gray-500">Özellik:</span>
              {([...FEATURES.map(([k, l]) => [k, l, ""]),
                ["none", "Kişiselleştirmesiz", "Ne kişiselleştirme ne de AI analizinde kişiselleştirme alanı var"],
                ["noAnalysis", "Analizsiz", "AI liste analizi yapılmamış"],
                ["conflict", "Çakışan alan", "Aynı bilgi hem listede/varyasyonda seçiliyor hem metin sorusunda yeniden isteniyor (ör. font)"],
              ] as Array<[string, string, string]>).map(([key, label, hint]) => {
                const on = featureFilter.includes(key);
                const n = allCategories.filter((c) => matchFeature(c, key)).length;
                if (!n && !on) return null;
                return (
                  <button key={key} title={hint || `${label} geçen ürünler (ad, eksen, AI analiz alanları)`}
                    onClick={(e) => { e.stopPropagation(); setFeatureFilter((f) => (on ? f.filter((x) => x !== key) : [...f, key])); }}
                    className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors ${
                      on ? (key === "conflict" ? "bg-red-100 text-red-700 ring-1 ring-red-300" : "bg-indigo-100 text-indigo-700 ring-1 ring-indigo-300")
                        : key === "conflict" ? "bg-red-50 text-red-700 hover:bg-red-100" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}>
                    {label} ({n})
                  </button>
                );
              })}
            </>)}
            {isJewelry && categories.length > 0 && (
              <button onClick={(e) => { e.stopPropagation(); setBulkInspiration(true); }}
                title="Listedeki (süzgeçteki) ürünler için eklentiye toplu ilham görevi ver"
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50">
                <Send className="h-3.5 w-3.5" /> Toplu ilham görevi ({categories.length})
              </button>
            )}
            {bulkInspiration && (
              <BulkInspirationModal catalogId={catalog.id}
                products={categories.map((c) => ({ id: c.id, name: c.name, hasInspiration: !!c.inspirations?.length }))}
                onClose={() => setBulkInspiration(false)} />
            )}
            <span className="mx-1 text-gray-300">|</span>
            {/* Pazar filtresi — yalnizca pazar bilgisi girilmis urun varsa */}
            {(marketCounts.us + marketCounts.tr + marketCounts.both) > 0 && (<>
            <span className="text-xs font-medium text-gray-500">Pazar:</span>
            {([
              ["", `Tümü (${allCategories.length})`],
              ["US", `ABD (${marketCounts.us + marketCounts.both})`],
              ["TR", `Türkiye (${marketCounts.tr + marketCounts.both})`],
            ] as Array<["" | "US" | "TR", string]>).map(([key, label]) => (
              <button
                key={key || "all"}
                onClick={(e) => { e.stopPropagation(); setMarketFilter(key); }}
                className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors ${
                  marketFilter === key
                    ? "bg-indigo-100 text-indigo-700 ring-1 ring-indigo-300"
                    : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                }`}
              >
                {label}
              </button>
            ))}
            {marketCounts.none > 0 && (
              <span className="text-[11px] text-gray-400" title="Pazar bilgisi girilmemiş ürünler her filtrede görünür">
                {marketCounts.none} üründe pazar bilgisi yok
              </span>
            )}
            <span className="mx-1 text-gray-300">|</span>
            </>)}

            <span className="text-xs font-medium text-gray-500">Kategori:</span>
            {groups.length === 0 && <span className="text-xs text-gray-400">henüz grup yok</span>}
            {groups.length > 0 && (
              <button
                onClick={(e) => { e.stopPropagation(); setGroupFilter(""); }}
                className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors ${!groupFilter ? "bg-indigo-100 text-indigo-700 ring-1 ring-indigo-300" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}
              >
                Tümü ({categories.length})
              </button>
            )}
            {groups.map((g) => (
              <span key={g} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${groupFilter === g ? "bg-indigo-100 text-indigo-700 ring-1 ring-indigo-300" : "bg-indigo-50 text-indigo-700"}`}>
                <button
                  title="Yalnız bu kategoriyi göster"
                  className="font-medium hover:underline"
                  onClick={(e) => { e.stopPropagation(); setGroupFilter(groupFilter === g ? "" : g); }}
                >
                  {g} ({categories.filter((c) => c.group?.trim() === g).length})
                </button>
                <button
                  title="Grup adını değiştir"
                  aria-label={`${g} grubunun adını değiştir`}
                  className="text-indigo-400 hover:text-indigo-700"
                  onClick={(e) => {
                    e.stopPropagation();
                    const to = prompt("Yeni grup adı:", g);
                    if (to?.trim() && to.trim() !== g) {
                      if (groupFilter === g) setGroupFilter(to.trim());
                      groupMutation.mutate({ renameGroup: { from: g, to: to.trim() } });
                    }
                  }}
                >
                  ✎
                </button>
                <button
                  title="Grubu sil (ürünler gruplanmamışa düşer)"
                  className="text-indigo-400 hover:text-red-500"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (confirm(`"${g}" grubu silinsin mi? İçindeki ürünler silinmez, gruplanmamışa düşer.`)) {
                      if (groupFilter === g) setGroupFilter("");
                      groupMutation.mutate({ removeGroup: g });
                    }
                  }}
                >
                  x
                </button>
              </span>
            ))}
            <button
              className="rounded-lg border border-dashed border-gray-300 px-2 py-0.5 text-xs text-gray-500 hover:border-indigo-400 hover:text-indigo-600"
              onClick={(e) => {
                e.stopPropagation();
                const name = prompt("Yeni grup adı:");
                if (name?.trim()) groupMutation.mutate({ addGroup: name.trim() });
              }}
            >
              + Grup ekle
            </button>
          </div>

          {grouped.length > 1 && (
            <div className="mb-2 flex justify-end gap-3 text-xs text-gray-500">
              <button className="hover:text-indigo-600" onClick={() => { const all = new Set(grouped.map(([g]) => g)); setCollapsed(all); try { localStorage.setItem(collapseKey, JSON.stringify([...all])); } catch { /* yok say */ } }}>Tümünü kapat</button>
              <button className="hover:text-indigo-600" onClick={() => { setCollapsed(new Set()); try { localStorage.removeItem(collapseKey); } catch { /* yok say */ } }}>Tümünü aç</button>
            </div>
          )}
          {grouped.map(([groupName, groupCats]) => {
            const isClosed = collapsed.has(groupName) && !groupFilter;
            return (
          <div key={groupName} className="mb-5">
            <button
              type="button"
              onClick={() => toggleCollapsed(groupName)}
              aria-expanded={!isClosed}
              className="mb-2 flex items-center gap-2 rounded-md text-left hover:text-indigo-600"
            >
              {isClosed ? <ChevronRight className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
              <h4 className="text-sm font-semibold text-gray-700">{groupName === UNGROUPED ? "Gruplanmamış" : groupName}</h4>
              <span className="text-xs text-gray-400">{groupCats.length} ürün</span>
            </button>
            {!isClosed && <>
            {groupCats.length === 0 && (
              <p className="mb-2 text-xs text-gray-400">Bu grup boş — ürünü açıp ayrıntı penceresindeki "Grup" seçiminden bu grubu seçin.</p>
            )}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {groupCats.map((cat: ProductCategory) => {
              const shot = shotByKey.get(shotProductKey(catalog.id, cat.id));
              const cover = coverOf(cat, shot?.coverUrl);
              const costs = cat.sizeOptions.map((s: { cost: number }) => s.cost).filter((c) => c > 0);
              const minCost = costs.length ? Math.min(...costs) : 0;
              const maxCost = costs.length ? Math.max(...costs) : 0;
              // Yalnizca secenegi olan eksenler: "3 Finish × 7 Length"
              const axes = (cat.source?.variationTypes ?? []).filter((t) => t.values.length > 1);
              const onEtsy = shot?.drafts.some((d) => d.etsyListingId);
              const hasDraft = (shot?.drafts.length ?? 0) > 0;
              const photoCount = shot?.shotCount ?? 0;

              return (
                <div key={cat.id} className="group flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white transition hover:border-indigo-300 hover:shadow-md">
                  {/* Kapak: uretilen fotograf > referans > tedarikci; tiklayinca ayrinti */}
                  <button
                    onClick={(e) => { e.stopPropagation(); setDetailProductId(cat.id); }}
                    title="Ürün ayrıntıları"
                    className="relative block aspect-square w-full overflow-hidden bg-gray-50"
                  >
                    {cover ? (
                      <img src={cover} alt={cat.name} loading="lazy" className="h-full w-full object-cover transition duration-200 group-hover:scale-[1.03]" />
                    ) : (
                      <span className="flex h-full w-full items-center justify-center text-xs text-gray-400">Görsel yok</span>
                    )}
                    {cat.discontinued ? (
                      <span className="absolute left-2 top-2 rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-semibold text-white shadow-sm" title={cat.discontinued.reason}>Tedarikçi kaldırdı</span>
                    ) : cat.priceUpdate && (
                      <span className="absolute right-2 top-2 rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-semibold text-white shadow-sm"
                        title={cat.priceUpdate.fetchedAt ? "Yeni maliyet çekildi; Listing Builder > Fiyatları güncelle ile Etsy fiyatını onaylayın" : "Eklentiden Rexven fiyatını yeniden çekin"}>
                        {cat.priceUpdate.fetchedAt
                          ? `Maliyet $${cat.priceUpdate.oldMinCost?.toFixed(2) ?? "?"} → $${cat.priceUpdate.newMinCost?.toFixed(2) ?? "?"}`
                          : "Fiyat güncellenecek"}
                      </span>
                    )}
                    {!cat.discontinued && (onEtsy || hasDraft) && (
                      <span className={`absolute left-2 top-2 rounded-full px-2 py-0.5 text-[10px] font-semibold shadow-sm ${onEtsy ? "bg-orange-500 text-white" : "bg-white/90 text-emerald-700"}`}>
                        {onEtsy ? "Etsy'de" : "Taslak var"}
                      </span>
                    )}
                    {isJewelry && photoCount > 0 && (
                      <span className="absolute bottom-2 right-2 rounded-full bg-black/60 px-2 py-0.5 text-[10px] text-white">{photoCount} foto</span>
                    )}
                  </button>

                  <div className="flex flex-1 flex-col p-2.5">
                    <button
                      onClick={(e) => { e.stopPropagation(); setDetailProductId(cat.id); }}
                      title={cat.name}
                      className="line-clamp-2 text-left text-sm font-medium leading-snug text-gray-900 hover:text-indigo-600"
                    >
                      {cat.name}
                    </button>
                    <p className="mt-1 flex items-center justify-between gap-2 text-[11px] text-gray-500">
                      <span className="truncate font-mono">{cat.source?.sku ?? ""}</span>
                      {maxCost > 0 && (
                        <span className="shrink-0 font-semibold text-gray-700 tabular-nums">
                          ${minCost.toFixed(2)}{maxCost !== minCost ? `–${maxCost.toFixed(2)}` : ""}
                        </span>
                      )}
                    </p>
                    {axes.length > 0 && (
                      <p className="mt-0.5 truncate text-[11px] text-gray-400" title={axes.map((t) => `${t.name}: ${t.values.join(", ")}`).join("\n")}>
                        {axes.map((t) => `${t.values.length} ${t.name}`).join(" × ")}
                      </p>
                    )}
                    {(() => {
                      const st = stateOf(cat);
                      if (st.hasDraft || (!st.noInspiration && !st.noPhoto)) return null;
                      return (
                        <div className="mt-1 flex flex-wrap gap-1" title="Liste yapmak için eksik">
                          {st.noInspiration && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">İlham listesi yok</span>}
                          {st.noPhoto && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">Fotoğraf yok</span>}
                        </div>
                      );
                    })()}

                    <div className="mt-auto flex gap-1.5 pt-2.5">
                      {isJewelry ? (
                        <Link
                          to={`/listing-builder/jewelry?catalogId=${catalog.id}&categoryId=${cat.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="flex-1 rounded-lg bg-orange-600 px-2 py-1.5 text-center text-xs font-medium text-white hover:bg-orange-700"
                        >
                          Listing Builder
                        </Link>
                      ) : (
                        <button
                          onClick={() => onBuildVariations(cat.id)}
                          className="flex-1 rounded-lg bg-indigo-600 px-2 py-1.5 text-xs font-medium text-white hover:bg-indigo-700"
                        >
                          Varyasyon Oluştur
                        </button>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); onEditPrices(cat.id); }}
                        title="Sabit satış fiyatlarını düzenle"
                        className="rounded-lg border border-gray-200 px-2 py-1.5 text-gray-500 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700"
                      >
                        <DollarSign className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); void deleteProduct(cat); }}
                        title="Ürünü katalogdan sil"
                        aria-label="Ürünü katalogdan sil"
                        className="rounded-lg border border-gray-200 px-2 py-1.5 text-gray-400 hover:border-red-300 hover:bg-red-50 hover:text-red-600"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
            </>}
          </div>
            );
          })}
        </div>
      )}

      {/* Urun ayrinti modali: eksen rolleri, Etsy basliklari, fiyat tablosu,
          kisisellestirme ve uretilen gorseller tek ekranda */}
      {lightbox && (
        <ImageLightbox
          urls={lightbox.urls}
          index={lightbox.index}
          onDelete={lightbox.onDelete}
          onClose={() => setLightbox(null)}
          onIndex={(i) => setLightbox((lb) => (lb ? { ...lb, index: i } : lb))}
        />
      )}

      {detailProductId && (() => {
        const cat = allCategories.find((c) => c.id === detailProductId);
        if (!cat) return null;
        return (
          <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4"
            onClick={() => setDetailProductId(null)}>
            <div className="my-6 w-full max-w-6xl rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()}>
              {(() => {
                const supplierImages = cat.source?.images ?? [];
                // Buyuk gorsel kartla ayni kapak; ardindan tedarikci fotograflari
                const detailCover = coverOf(cat, shotByKey.get(shotProductKey(catalog.id, cat.id))?.coverUrl);
                const images = detailCover && !supplierImages.includes(detailCover) ? [detailCover, ...supplierImages] : supplierImages;
                const attrs = productAttributes(cat);
                const single = cat.sizeOptions.length === 1 ? cat.sizeOptions[0] : null;
                const brandShown = cat.brand && cat.brand.trim() !== cat.name.trim() ? cat.brand : null;
                return (
                  <>
                    {/* Ust: buyuk gorsel + temel bilgiler */}
                    <div className="mb-5 flex items-start justify-between gap-4">
                      <h3 className="text-xl font-bold leading-snug text-gray-900">{cat.name}</h3>
                      <button onClick={() => setDetailProductId(null)} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100">
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[340px_1fr]">
                      <div>
                        {images[0] ? (
                          <button onClick={() => setLightbox({ urls: images, index: 0 })} className="block w-full overflow-hidden rounded-xl border border-gray-200 bg-gray-50">
                            <img src={images[0]} alt="" className="aspect-square w-full cursor-zoom-in object-contain" />
                          </button>
                        ) : (
                          <div className="flex aspect-square w-full items-center justify-center rounded-xl border border-dashed border-gray-300 text-sm text-gray-400">Görsel yok</div>
                        )}
                        {images.length > 1 && (
                          <div className="mt-2 grid grid-cols-5 gap-1.5">
                            {images.slice(1, 11).map((u, i) => (
                              <button key={u} onClick={() => setLightbox({ urls: images, index: i + 1 })}
                                className="overflow-hidden rounded-lg border border-gray-200 bg-gray-50 hover:border-indigo-300">
                                <img src={u} alt="" className="aspect-square w-full object-cover" loading="lazy" />
                              </button>
                            ))}
                          </div>
                        )}
                        <div className="mt-3">
                          <ProductShotStrip catalogId={catalog.id} categoryId={cat.id}
                            onOpenLightbox={(urls, i, onDelete) => setLightbox({ urls, index: i, onDelete })}
                            onCloseLightbox={() => setLightbox(null)} />
                        </div>
                      </div>

                      <div className="min-w-0 space-y-5 text-sm">
                        <div className="flex flex-wrap items-center gap-2">
                          {cat.source?.sku && <span className="rounded-md bg-gray-900 px-2 py-0.5 text-xs font-bold text-white">{cat.source.sku}</span>}
                          {brandShown && <span className="text-gray-600">{brandShown}</span>}
                          {cat.source?.url && (
                            <a href={cat.source.url} target="_blank" rel="noreferrer" className="font-medium text-indigo-600 hover:underline">Rexven'de aç ↗</a>
                          )}
                          <span className="ml-auto flex items-center gap-2">
                            <label className="flex items-center gap-1 text-xs text-gray-500">
                              Grup
                              <select
                                value={cat.group?.trim() || ""}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (val === "__new__") {
                                    const name = prompt("Yeni grup adı:");
                                    if (name?.trim()) groupMutation.mutate({ assignments: [{ categoryId: cat.id, group: name.trim() }] });
                                    return;
                                  }
                                  groupMutation.mutate({ assignments: [{ categoryId: cat.id, group: val || null }] });
                                }}
                                className="rounded-lg border border-gray-200 px-2 py-1 text-xs text-gray-700"
                              >
                                <option value="">— gruplanmamış —</option>
                                {groups.map((g) => <option key={g} value={g}>{g}</option>)}
                                <option value="__new__">+ Yeni grup…</option>
                              </select>
                            </label>
                            {isJewelry && (
                              <Link to={`/listing-builder/jewelry?catalogId=${catalog.id}&categoryId=${cat.id}`}
                                className="rounded-lg bg-orange-600 px-3 py-1 text-xs font-medium text-white hover:bg-orange-700">
                                Listing Builder
                              </Link>
                            )}
                          </span>
                        </div>

                        {/* Fiyat */}
                        <div>
                          <p className="mb-1.5 text-sm font-semibold text-gray-800">Maliyet</p>
                          {single ? (
                            <p className="text-2xl font-bold tabular-nums text-gray-900">
                              ${single.cost.toFixed(2)}
                              {single.retailPrice != null && <span className="ml-3 text-base font-semibold text-emerald-700">sabit satış ${single.retailPrice.toFixed(2)}</span>}
                            </p>
                          ) : (
                            <div className="max-h-72 overflow-y-auto rounded-xl border border-gray-200">
                              <table className="w-full text-sm">
                                <thead className="bg-gray-50 text-gray-500">
                                  <tr>
                                    <th className="px-3 py-1.5 text-left font-medium">Değer</th>
                                    <th className="px-3 py-1.5 text-right font-medium">Maliyet</th>
                                    <th className="px-3 py-1.5 text-right font-medium">Sabit satış</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {cat.sizeOptions.map((so) => (
                                    <tr key={so.size} className="border-t border-gray-100">
                                      <td className="px-3 py-1.5 text-gray-800">
                                        {so.size}
                                        {cat.axisValueLabels && Object.values(cat.axisValueLabels).map((m) => m[so.size]).find(Boolean) && (
                                          <span className="ml-1.5 text-xs text-indigo-600">→ {Object.values(cat.axisValueLabels).map((m) => m[so.size]).find(Boolean)}</span>
                                        )}
                                      </td>
                                      <td className="px-3 py-1.5 text-right tabular-nums text-gray-700">${so.cost.toFixed(2)}</td>
                                      <td className="px-3 py-1.5 text-right tabular-nums text-emerald-700">
                                        {so.retailPrice != null ? `$${so.retailPrice.toFixed(2)}` : "—"}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                          <button onClick={() => { setDetailProductId(null); onEditPrices(cat.id); }}
                            className="mt-2 rounded-lg border border-indigo-200 px-3 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-50">
                            Fiyatları düzenle
                          </button>
                        </div>

                        {isJewelry && (
                          <div className="grid gap-4 sm:grid-cols-2">
                            <div>
                              <p className="mb-1.5 text-sm font-semibold text-gray-800">Ürün tipi</p>
                              <ProductTypePicker catalogId={catalog.id} cat={cat} />
                            </div>
                            <div>
                              <p className="mb-1.5 text-sm font-semibold text-gray-800">Gerçek ölçü (mm)</p>
                              <PhysicalSizeEditor catalogId={catalog.id} cat={cat} />
                            </div>
                            <div>
                              <p className="mb-1.5 text-sm font-semibold text-gray-800">Kargo maliyeti (USD)</p>
                              <ShippingCostEditor catalogId={catalog.id} cat={cat} />
                            </div>
                            <div className="sm:col-span-2">
                              <p className="mb-1.5 text-sm font-semibold text-gray-800">İlham listeleri</p>
                              <InspirationsEditor catalogId={catalog.id} cat={cat} />
                              <div className="mt-2"><InspirationTaskButton catalogId={catalog.id} categoryId={cat.id} productName={cat.name} /></div>
                            </div>
                          </div>
                        )}

                        {/* Ozellikler: tek degerli eksenler + olcu (liste aciklamasina girer) */}
                        {Object.keys(attrs).length > 0 && (
                          <div>
                            <p className="mb-1.5 text-sm font-semibold text-gray-800">Özellikler</p>
                            <div className="flex flex-wrap gap-1.5">
                              {Object.entries(attrs).map(([k, v]) => (
                                <span key={k} className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs text-gray-700">
                                  <b className="font-semibold">{k}</b>: {v}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {cat.colorAvailability.length > 0 && (
                          <div>
                            <p className="mb-1.5 text-sm font-semibold text-gray-800">Renkler</p>
                            <div className="flex flex-wrap gap-1.5">
                              {cat.colorAvailability.map((c) => (
                                <span key={c.color} className="rounded-full border border-gray-200 px-3 py-1 text-xs text-gray-700">{c.color}</span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* AI liste analizi: alanlar, chart'lar, talimat — onay/duzeltme ile ogrenir */}
                        <ListingAnalysisPanel catalogId={catalog.id} cat={cat} />

                        {/* Secenekler: listede varyasyon / kisisellestirme */}
                        <div>
                          <p className="mb-1.5 text-sm font-semibold text-gray-800">Seçenekler</p>
                          <ProductOptionsEditor catalogId={catalog.id} cat={cat} startOpen />
                        </div>

                        {(cat.source?.descriptionText || cat.source?.descriptionHtml) && (
                          <details className="rounded-xl border border-gray-200 p-3">
                            <summary className="cursor-pointer text-sm font-semibold text-gray-800">Tedarikçi açıklaması</summary>
                            <p className="mt-2 whitespace-pre-line text-xs leading-relaxed text-gray-600">
                              {cat.source?.descriptionText || descriptionPlainText(cat.source?.descriptionHtml)}
                            </p>
                          </details>
                        )}


                      </div>
                    </div>

                    <div className="mt-6 flex justify-end">
                      <button onClick={() => setDetailProductId(null)}
                        className="rounded-lg bg-indigo-600 px-5 py-2 text-sm font-medium text-white hover:bg-indigo-700">
                        Kapat
                      </button>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ============================================
// Import Modal
// ============================================

function ImportModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState("");
  const [supplier, setSupplier] = useState("");
  const [description, setDescription] = useState("");
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<any[] | null>(null);
  const [previewing, setPreviewing] = useState(false);

  const handlePreview = async () => {
    if (!file) return;
    setPreviewing(true);
    setError(null);
    try {
      const result = await apiClient.previewSupplierExcel(file);
      setPreview(result.summary);
      if (!name) setName(file.name.replace(/\.[^.]+$/, ""));
    } catch (err: any) {
      setError(err.message || "Önizleme başarısız");
    } finally {
      setPreviewing(false);
    }
  };

  const handleImport = async () => {
    if (!file || !name) return;
    setImporting(true);
    setError(null);
    try {
      await apiClient.importSupplierCatalogExcel(file, name, supplier, description);
      onSuccess();
    } catch (err: any) {
      setError(err.message || "Import başarısız");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900">Excel Katalog Yükle</h2>
          <button onClick={onClose} className="rounded-lg p-1 hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* File Upload */}
        <div className="mb-4">
          <label className="mb-1 block text-sm font-medium text-gray-700">Excel Dosyası</label>
          <div className="flex items-center gap-2">
            <label className="flex flex-1 cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-gray-300 p-4 hover:border-indigo-400 hover:bg-indigo-50/30">
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) { setFile(f); setPreview(null); }
                }}
              />
              <div className="text-center">
                <FileSpreadsheet className="mx-auto h-8 w-8 text-gray-400" />
                <p className="mt-1 text-sm text-gray-600">
                  {file ? file.name : "Dosya seçin veya sürükleyin"}
                </p>
              </div>
            </label>
            {file && !preview && (
              <button
                onClick={handlePreview}
                disabled={previewing}
                className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-50"
              >
                {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : "Önizle"}
              </button>
            )}
          </div>
        </div>

        {/* Preview */}
        {preview && (
          <div className="mb-4 max-h-48 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-3">
            <p className="mb-2 text-xs font-semibold text-gray-500 uppercase">Bulunan Kategoriler</p>
            <div className="space-y-1.5">
              {preview.map((cat: any, i: number) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="font-medium text-gray-800">{cat.name} <span className="text-gray-400">({cat.brand})</span></span>
                  <div className="flex gap-2 text-xs text-gray-500">
                    <span>{cat.sizeCount} beden</span>
                    <span className="text-green-600">{cat.inStockColorCount} renk stokta</span>
                    <span>${cat.priceRange.min.toFixed(2)}-${cat.priceRange.max.toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Form Fields */}
        <div className="mb-4 grid gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Katalog Adı *</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="örn: Printonomi Clothing"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Tedarikçi</label>
            <input
              value={supplier}
              onChange={(e) => setSupplier(e.target.value)}
              placeholder="örn: Printonami"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
            />
          </div>
        </div>
        <div className="mb-4">
          <label className="mb-1 block text-sm font-medium text-gray-700">Açıklama</label>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="örn: POD kıyafet tedarikçi kataloğu"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
          />
        </div>

        {error && (
          <div className="mb-4 rounded-lg bg-red-50 px-4 py-2 text-sm text-red-600">{error}</div>
        )}

        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            İptal
          </button>
          <button
            onClick={handleImport}
            disabled={!file || !name || importing}
            className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            İçeri Aktar
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Variation Builder Modal
// ============================================

function VariationBuilderModal({
  catalog,
  categoryId,
  onClose,
}: {
  catalog: SupplierCatalog;
  categoryId: string;
  onClose: () => void;
}) {
  const categories = catalog.categories as ProductCategory[];
  const category = categories.find((c) => c.id === categoryId);

  const [selectedSizes, setSelectedSizes] = useState<string[]>([]);
  const [selectedColors, setSelectedColors] = useState<string[]>([]);
  const [sizePrefix, setSizePrefix] = useState("");
  const [onlyInStock, setOnlyInStock] = useState(true);
  const [pricingMode, setPricingMode] = useState<PricingMode>("margin_percent");
  const [marginPercent, setMarginPercent] = useState(40);
  const [fixedProfit, setFixedProfit] = useState(15);
  const [targetBasePrice, setTargetBasePrice] = useState(39.99);
  const [shippingCost, setShippingCost] = useState(DEFAULT_ETSY_SHIPPING_USD);
  const [shippingCharge, setShippingCharge] = useState(0);
  const [offsiteAds, setOffsiteAds] = useState(false);
  const [discountPct, setDiscountPct] = useState(DEFAULT_DISCOUNT_PCT);
  const [otherCosts, setOtherCosts] = useState(0);

  const [result, setResult] = useState<GeneratedVariationResult | null>(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Initialize with all sizes selected and prefix from category name
  useEffect(() => {
    if (category) {
      setSelectedSizes(category.sizeOptions.map((s: { size: string }) => s.size));
      // Auto-suggest prefix
      const nameMap: Record<string, string> = {
        "ADULT HOODIE": "Hoodie - ",
        "ADULT SWEATSHIRT": "Sweatshirt - ",
        "ADULT-UNISEX SHIRT": "Shirt - ",
        "WOMEN V-NECK": "V-Neck - ",
        "YOUTH SHIRT": "Youth - ",
        "TODDLER SHIRT": "Toddler - ",
        "BABY BODYSUIT": "Bodysuit - ",
        "WOMEN TANK": "Tank - ",
        "YOUTH SWEATSHIRT": "Youth Sweatshirt - ",
        "TODDLER SWEATSHIRT": "Toddler Sweatshirt - ",
        "LONG SLEEVE": "Long Sleeve - ",
        "WOMEN SHIRT": "Women Shirt - ",
      };
      const upper = category.name.toUpperCase();
      setSizePrefix(nameMap[upper] || "");

      // Select all in-stock colors
      const inStock = category.colorAvailability
        .filter((c: { sizes: Record<string, boolean> }) => Object.values(c.sizes).some((v: boolean) => v))
        .map((c: { color: string }) => c.color);
      setSelectedColors(inStock);
    }
  }, [category]);

  const availableColors = useMemo(() => {
    if (!category) return [];
    return category.colorAvailability.map((c: { color: string; sizes: Record<string, boolean> }) => {
      const hasStock = Object.values(c.sizes).some((v: boolean) => v);
      return { ...c, hasStock };
    });
  }, [category]);



  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const pricing: PricingStrategy = {
        mode: pricingMode,
        marginPercent: pricingMode === "margin_percent" ? marginPercent : undefined,
        fixedProfit: pricingMode === "fixed_profit" ? fixedProfit : undefined,
        targetBasePrice: pricingMode === "target_price" ? targetBasePrice : undefined,
        shippingCost,
        otherCosts,
        shippingCharge,
        offsiteAds,
        discountPct,
      };

      const res = await apiClient.generateVariationsFromCatalog({
        catalogId: catalog.id,
        categoryId,
        selectedColors: selectedColors.length > 0 ? selectedColors : undefined,
        selectedSizes: selectedSizes.length > 0 ? selectedSizes : undefined,
        sizePrefix,
        pricing,
        onlyInStock,
      });

      setResult(res);
    } catch (err: any) {
      setError(err.message || "Varyasyon oluşturma başarısız");
    } finally {
      setGenerating(false);
    }
  };

  const handleCopyToClipboard = () => {
    if (!result) return;
    const json = JSON.stringify(result.variations, null, 2);
    navigator.clipboard.writeText(json);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSaveAsTemplate = async () => {
    if (!result || !category) return;
    const defaultName = `${category.name} - ${category.brand}`;
    const name = prompt("Şablon adı:", defaultName);
    if (!name) return;
    try {
      await apiClient.createVariationTemplate({
        name,
        brand: category.brand || null,
        category: "supplier-catalog",
        variations: result.variations,
        syncSource: {
          catalogId: catalog.id,
          categories: [{ categoryId, sizePrefix, selectedSizes: selectedSizes.length ? selectedSizes : null, selectedColors: selectedColors.length ? selectedColors : null }],
        },
      });
      alert("Varyasyon şablonu kaydedildi!");
    } catch {
      alert("Kaydetme başarısız");
    }
  };

  if (!category) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 pt-10">
      <div className="w-full max-w-4xl rounded-2xl bg-white p-6 shadow-2xl">
        {/* Header */}
        <div className="mb-5 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Varyasyon Oluşturucu</h2>
            <p className="text-sm text-gray-500">
              {category.name} - {category.brand} | {catalog.name}
            </p>
          </div>
          <button onClick={onClose} className="rounded-lg p-1 hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Left: Configuration */}
          <div className="space-y-4">
            {/* Size Prefix */}
            <div>
              <label className="mb-1 flex items-center gap-1 text-sm font-medium text-gray-700">
                <Settings2 className="h-3.5 w-3.5" />
                Beden Prefix
              </label>
              <input
                value={sizePrefix}
                onChange={(e) => setSizePrefix(e.target.value)}
                placeholder='örn: "Hoodie - " → "Hoodie - Small"'
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
              />
              <p className="mt-1 text-xs text-gray-400">
                Örnek: {sizePrefix ? `"${sizePrefix}${category.sizeOptions[0]?.size}"` : `"${category.sizeOptions[0]?.size}"`}
              </p>
            </div>

            {/* Size Selection */}
            <div>
              <label className="mb-1 flex items-center justify-between text-sm font-medium text-gray-700">
                <span>Bedenler ({selectedSizes.length}/{category.sizeOptions.length})</span>
                <button
                  onClick={() =>
                    setSelectedSizes(
                      selectedSizes.length === category.sizeOptions.length
                        ? []
                        : category.sizeOptions.map((s) => s.size)
                    )
                  }
                  className="text-xs text-indigo-600 hover:underline"
                >
                  {selectedSizes.length === category.sizeOptions.length ? "Hiçbiri" : "Tümünü Seç"}
                </button>
              </label>
              <div className="flex flex-wrap gap-1.5">
                {category.sizeOptions.map((s) => {
                  const isSelected = selectedSizes.includes(s.size);
                  return (
                    <button
                      key={s.size}
                      onClick={() =>
                        setSelectedSizes(
                          isSelected
                            ? selectedSizes.filter((sz) => sz !== s.size)
                            : [...selectedSizes, s.size]
                        )
                      }
                      className={`rounded-lg border px-2.5 py-1 text-xs font-medium transition-colors ${
                        isSelected
                          ? "border-indigo-300 bg-indigo-50 text-indigo-700"
                          : "border-gray-200 text-gray-500 hover:border-gray-300"
                      }`}
                    >
                      {s.size} <span className="text-gray-400">${s.cost.toFixed(2)}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Color Selection */}
            <div>
              <label className="mb-1 flex items-center justify-between text-sm font-medium text-gray-700">
                <span className="flex items-center gap-1">
                  <Filter className="h-3.5 w-3.5" />
                  Renkler ({selectedColors.length}/{availableColors.length})
                </span>
                <div className="flex gap-2">
                  <label className="flex items-center gap-1 text-xs text-gray-500">
                    <input
                      type="checkbox"
                      checked={onlyInStock}
                      onChange={(e) => setOnlyInStock(e.target.checked)}
                      className="rounded"
                    />
                    Sadece stokta
                  </label>
                  <button
                    onClick={() => {
                      const targets = onlyInStock
                        ? availableColors.filter((c) => c.hasStock).map((c) => c.color)
                        : availableColors.map((c) => c.color);
                      setSelectedColors(
                        selectedColors.length === targets.length ? [] : targets
                      );
                    }}
                    className="text-xs text-indigo-600 hover:underline"
                  >
                    Tümünü Seç/Kaldır
                  </button>
                </div>
              </label>
              <div className="max-h-40 overflow-y-auto rounded-lg border border-gray-200 p-2">
                <div className="flex flex-wrap gap-1.5">
                  {availableColors
                    .filter((c) => !onlyInStock || c.hasStock)
                    .map((c) => {
                      const isSelected = selectedColors.includes(c.color);
                      return (
                        <button
                          key={c.color}
                          onClick={() =>
                            setSelectedColors(
                              isSelected
                                ? selectedColors.filter((cl) => cl !== c.color)
                                : [...selectedColors, c.color]
                            )
                          }
                          className={`flex items-center gap-1 rounded-lg border px-2 py-1 text-xs transition-colors ${
                            isSelected
                              ? "border-green-300 bg-green-50 text-green-700"
                              : "border-gray-200 text-gray-500 hover:border-gray-300"
                          } ${!c.hasStock ? "opacity-40" : ""}`}
                        >
                          {isSelected && <Check className="h-3 w-3" />}
                          {c.color}
                        </button>
                      );
                    })}
                </div>
              </div>
            </div>

            {/* Pricing Configuration */}
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <label className="mb-2 flex items-center gap-1 text-sm font-medium text-gray-700">
                <DollarSign className="h-3.5 w-3.5" />
                Fiyatlandırma
              </label>

              {/* Mode Selection */}
              <div className="mb-3 flex gap-1 rounded-lg bg-gray-200 p-0.5">
                {[
                  { mode: "margin_percent" as PricingMode, label: "% Marj" },
                  { mode: "fixed_profit" as PricingMode, label: "Sabit Kâr" },
                  { mode: "target_price" as PricingMode, label: "Hedef Fiyat" },
                ].map((m) => (
                  <button
                    key={m.mode}
                    onClick={() => setPricingMode(m.mode)}
                    className={`flex-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${
                      pricingMode === m.mode
                        ? "bg-white text-gray-900 shadow-sm"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>

              {/* Mode-specific input */}
              <div className="mb-3">
                {pricingMode === "margin_percent" && (
                  <div>
                    <label className="text-xs text-gray-600">Hedef Kâr Marjı (%)</label>
                    <input
                      type="number"
                      value={marginPercent}
                      onChange={(e) => setMarginPercent(Number(e.target.value))}
                      className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                      min={1}
                      max={90}
                    />
                  </div>
                )}
                {pricingMode === "fixed_profit" && (
                  <div>
                    <label className="text-xs text-gray-600">Sabit Kâr ($)</label>
                    <input
                      type="number"
                      value={fixedProfit}
                      onChange={(e) => setFixedProfit(Number(e.target.value))}
                      className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                      min={0}
                      step={0.5}
                    />
                  </div>
                )}
                {pricingMode === "target_price" && (
                  <div>
                    <label className="text-xs text-gray-600">Hedef Baz Fiyat ($)</label>
                    <input
                      type="number"
                      value={targetBasePrice}
                      onChange={(e) => setTargetBasePrice(Number(e.target.value))}
                      className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                      min={0}
                      step={0.01}
                    />
                  </div>
                )}
              </div>

              {/* Common costs */}
              <div className="grid grid-cols-2 gap-2">
                <EtsyShippingInputs
                  shippingCharge={shippingCharge} setShippingCharge={setShippingCharge}
                  shippingCost={shippingCost} setShippingCost={setShippingCost}
                  offsiteAds={offsiteAds} setOffsiteAds={setOffsiteAds}
                  discountPct={discountPct} setDiscountPct={setDiscountPct}
                />
                <div>
                  <label className="text-xs text-gray-600">Diğer Giderler ($)</label>
                  <input
                    type="number"
                    value={otherCosts}
                    onChange={(e) => setOtherCosts(Number(e.target.value))}
                    className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                    step={0.5}
                  />
                </div>
              </div>
            </div>

            {/* Generate Button */}
            <button
              onClick={handleGenerate}
              disabled={generating || selectedSizes.length === 0 || selectedColors.length === 0}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {generating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Calculator className="h-4 w-4" />
              )}
              Varyasyonları Hesapla ({selectedSizes.length} x {selectedColors.length} = {selectedSizes.length * selectedColors.length})
            </button>

            {error && (
              <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</div>
            )}
          </div>

          {/* Right: Results */}
          <div>
            {result ? (
              <div className="space-y-4">
                {/* Summary */}
                <div className="rounded-lg border border-green-200 bg-green-50 p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-green-800">
                        {result.totalCombinations} Varyasyon Oluşturuldu
                      </p>
                      <p className="text-xs text-green-600">
                        Baz Fiyat: ${result.basePrice.toFixed(2)}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={handleCopyToClipboard}
                        className="flex items-center gap-1 rounded-lg bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                      >
                        {copied ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
                        {copied ? "Kopyalandı" : "JSON Kopyala"}
                      </button>
                      <button
                        onClick={handleSaveAsTemplate}
                        className="flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700"
                      >
                        Şablon Kaydet
                      </button>
                    </div>
                  </div>
                </div>

                {/* Pricing Breakdown */}
                <div>
                  <p className="mb-2 text-sm font-medium text-gray-700">Fiyat Tablosu</p>
                  <div className="overflow-hidden rounded-lg border border-gray-200">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="px-3 py-2 text-left font-medium text-gray-600">Beden</th>
                          <th className="px-3 py-2 text-right font-medium text-gray-600">Maliyet</th>
                          <th className="px-3 py-2 text-right font-medium text-gray-600">Liste</th>
                          <th className="px-3 py-2 text-right font-medium text-gray-600">İndirimli</th>
                          {shippingCharge > 0 && <th className="px-3 py-2 text-right font-medium text-gray-600" title="Alıcının ödediği: indirimli satış + kargo">Satış + kargo</th>}
                          <th className="px-3 py-2 text-right font-medium text-gray-600">Kâr</th>
                          <th className="px-3 py-2 text-right font-medium text-gray-600">Marj</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.pricingBreakdown.map((p, i) => (
                          <tr key={i} className={i % 2 === 0 ? "" : "bg-gray-50"}>
                            <td className="px-3 py-1.5 font-medium text-gray-800">
                              {sizePrefix}{p.size}
                            </td>
                            <td className="px-3 py-1.5 text-right text-gray-600">${p.cost.toFixed(2)}</td>
                            <td className="px-3 py-1.5 text-right font-medium text-gray-900">${p.sellingPrice.toFixed(2)}</td>
                            <td className="px-3 py-1.5 text-right text-gray-600">${discountedPrice(p.sellingPrice, discountPct).toFixed(2)}</td>
                            {shippingCharge > 0 && <td className="px-3 py-1.5 text-right text-gray-600">${discountedPrice(p.sellingPrice, discountPct).toFixed(2)} + ${shippingCharge.toFixed(2)}</td>}
                            <td className="px-3 py-1.5 text-right text-green-600">${p.profit.toFixed(2)}</td>
                            <td className="px-3 py-1.5 text-right">
                              <span className={`rounded px-1.5 py-0.5 ${
                                p.profitMargin >= 30 ? "bg-green-100 text-green-700" :
                                p.profitMargin >= 20 ? "bg-yellow-100 text-yellow-700" :
                                "bg-red-100 text-red-700"
                              }`}>
                                {p.profitMargin.toFixed(1)}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Generated Variations Preview */}
                <div>
                  <p className="mb-2 text-sm font-medium text-gray-700">Etsy Varyasyon Önizleme</p>
                  {result.variations.map((v, vi) => (
                    <div key={vi} className="mb-2">
                      <p className="mb-1 text-xs font-semibold text-gray-500 uppercase">{v.name}</p>
                      <div className="flex flex-wrap gap-1">
                        {v.options.map((opt, oi) => (
                          <span
                            key={oi}
                            className="rounded border border-gray-200 bg-white px-2 py-0.5 text-xs text-gray-700"
                          >
                            {opt.label}
                            {opt.priceAdjust ? ` (+$${opt.priceAdjust.toFixed(2)})` : ""}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Info */}
                <div className="flex items-start gap-2 rounded-lg bg-blue-50 p-3 text-xs text-blue-700">
                  <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                  <p>
                    "Şablon Kaydet" ile bu varyasyonu kaydedin, sonra Listing Builder'da
                    şablonu seçip listing'e uygulayabilirsiniz. Etsy'de "Prices vary for each Size
                    and Color" seçeneğine denk gelir.
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex h-full items-center justify-center rounded-lg border-2 border-dashed border-gray-200 p-8">
                <div className="text-center">
                  <Calculator className="mx-auto h-10 w-10 text-gray-300" />
                  <p className="mt-2 text-sm text-gray-500">
                    Beden ve renkleri seçin, fiyatlandırmayı ayarlayın ve "Hesapla" butonuna basın
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Combo Variation Builder Modal
// ============================================

const SIZE_PREFIX_MAP: Record<string, string> = {
  "ADULT HOODIE": "Hoodie - ",
  "ADULT SWEATSHIRT": "Sweatshirt - ",
  "ADULT-UNISEX SHIRT": "Shirt - ",
  "WOMEN V-NECK": "V-Neck - ",
  "YOUTH SHIRT": "Youth - ",
  "TODDLER SHIRT": "Toddler - ",
  "BABY BODYSUIT": "Bodysuit - ",
  "WOMEN TANK": "Tank - ",
  "YOUTH SWEATSHIRT": "Youth Sweatshirt - ",
  "TODDLER SWEATSHIRT": "Toddler Sweatshirt - ",
  "LONG SLEEVE": "Long Sleeve - ",
  "WOMEN SHIRT": "Women Shirt - ",
};

/** Ortak kargo/kesinti girdileri: alicidan alinan kargo (0 = ucretsiz), tedarikci
 *  kargosu (maliyet) ve Offsite Ads. Etsy kesintileri ortak modelden otomatik. */
function EtsyShippingInputs(p: {
  shippingCharge: number; setShippingCharge: (n: number) => void;
  shippingCost: number; setShippingCost: (n: number) => void;
  offsiteAds: boolean; setOffsiteAds: (b: boolean) => void;
  discountPct: number; setDiscountPct: (n: number) => void;
}) {
  const M = ETSY_FEE_MODEL;
  return (
    <>
      <div>
        <label className="text-xs text-gray-600">Alıcıdan alınan kargo ($)</label>
        <div className="flex gap-1">
          <input type="number" min={0} step={0.5} value={p.shippingCharge} onChange={(e) => p.setShippingCharge(Math.max(0, Number(e.target.value)))}
            aria-label="Alıcıdan alınan kargo" className="w-full min-w-0 rounded border border-gray-300 px-2 py-1.5 text-sm" />
          <button type="button" onClick={() => p.setShippingCharge(0)}
            className={`shrink-0 rounded border px-2 text-xs ${p.shippingCharge === 0 ? "border-green-300 bg-green-50 text-green-700" : "border-gray-300 text-gray-600"}`}>
            Ücretsiz
          </button>
        </div>
      </div>
      <div>
        <label className="text-xs text-gray-600" title="Katalogda bedene ya da ürüne ait kargo varsa o kullanılır">Tedarikçi kargosu ($)</label>
        <input type="number" min={0} step={0.5} value={p.shippingCost} onChange={(e) => p.setShippingCost(Number(e.target.value))}
          aria-label="Tedarikçi kargosu" className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm" />
      </div>
      <div className="col-span-2">
        <label className="text-xs text-gray-600" title="Etsy'ye liste fiyatı gider; satış bu oranda indirimle olur. Kâr indirimli fiyat üzerinden hesaplanır.">İndirim oranı (%)</label>
        <input type="number" min={0} max={90} step={5} value={p.discountPct} onChange={(e) => p.setDiscountPct(Math.min(90, Math.max(0, Number(e.target.value))))}
          aria-label="İndirim oranı" className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm" />
      </div>
      <p className="col-span-2 text-[10px] text-gray-500">
        Satış fiyatı liste fiyatıdır; alıcı %{p.discountPct} indirimle öder, kâr indirimli fiyattan hesaplanır.
        Etsy kesintileri otomatik: işlem %{M.transactionPct} + ödeme %{M.paymentPct} + ${M.paymentFixed} + regulatory %{M.regulatoryPct}, bunların KDV'si %{M.vatPct}, yenileme ${M.renewFee}.
        Kargo ücretsizse tedarikçi kargosu ürün fiyatına biner.
      </p>
      <label className="col-span-2 flex items-center gap-2 text-xs text-gray-700">
        <input type="checkbox" checked={p.offsiteAds} onChange={(e) => p.setOffsiteAds(e.target.checked)} />
        Offsite Ads kesintisini de hesaba kat (%{M.offsiteAdsPct} + KDV)
      </label>
    </>
  );
}

interface ComboEntry {
  categoryId: string;
  sizePrefix: string;
  /** Bedenin elle verilen Etsy adi; yoksa prefix + beden */
  sizeLabels: Record<string, string>;
  selectedSizes: string[];
  selectedColors: string[];
  expanded: boolean;
}

function ComboVariationBuilderModal({
  catalog,
  onClose,
}: {
  catalog: SupplierCatalog;
  onClose: () => void;
}) {
  const categories = (catalog.categories ?? []) as ProductCategory[];

  // Combo entries - user adds categories here (each has its own selectedColors)
  const [entries, setEntries] = useState<ComboEntry[]>([]);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [pricingMode, setPricingMode] = useState<PricingMode>("margin_percent");
  const [marginPercent, setMarginPercent] = useState(40);
  const [fixedProfit, setFixedProfit] = useState(15);
  const [targetBasePrice, setTargetBasePrice] = useState(39.99);
  const [shippingCost, setShippingCost] = useState(DEFAULT_ETSY_SHIPPING_USD);
  const [shippingCharge, setShippingCharge] = useState(0);
  const [offsiteAds, setOffsiteAds] = useState(false);
  const [discountPct, setDiscountPct] = useState(DEFAULT_DISCOUNT_PCT);
  const [otherCosts, setOtherCosts] = useState(0);

  const [result, setResult] = useState<ComboVariationResult | null>(null);
  const [editedBreakdown, setEditedBreakdown] = useState<ComboPricedVariant[]>([]);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [bulkPrice, setBulkPrice] = useState<Record<string, string>>({}); // categoryId -> price input
  // 1. eksenin Etsy basligi + baski yuzu 2. ekseni (renksiz urunler: ornament)
  const [sizeAxisName, setSizeAxisName] = useState("");
  const [printAxisOn, setPrintAxisOn] = useState(true);
  const [printAxis, setPrintAxis] = useState({ name: "Print Side", single: "Single-sided", double: "Double-sided" });
  const [doubleAdjust, setDoubleAdjust] = useState<number | null>(null); // cift yuz farki (elle)

  // Add a new category entry
  const addCategory = (catId: string) => {
    if (entries.some((e) => e.categoryId === catId)) return;
    const cat = categories.find((c: ProductCategory) => c.id === catId);
    if (!cat) return;
    const prefix = SIZE_PREFIX_MAP[cat.name.toUpperCase()] || `${cat.name} - `;
    const allSizes = cat.sizeOptions.map((s: { size: string }) => s.size);
    // Auto-select in-stock colors for this category
    const inStockColors = cat.colorAvailability
      .filter((ca: { color: string; sizes: Record<string, boolean> }) => allSizes.some((sz: string) => ca.sizes[sz] === true))
      .map((ca: { color: string }) => ca.color);
    // Tek "Standart" bedenli urunde (ornament) "Glass Ornament - Standart"
    // yerine yalniz urun adi: "Glass Ornament"
    const sizeLabels: Record<string, string> = allSizes.length === 1 && allSizes[0] === "Standart"
      ? { Standart: prefix.replace(/\s*-\s*$/, "") } : {};
    setEntries([...entries, {
      categoryId: catId,
      sizePrefix: prefix,
      sizeLabels,
      selectedSizes: allSizes,
      selectedColors: inStockColors,
      expanded: true,
    }]);
    setResult(null);
  };

  const removeCategory = (catId: string) => {
    setEntries(entries.filter((e) => e.categoryId !== catId));
    setResult(null);
  };

  const updateEntry = (catId: string, updates: Partial<ComboEntry>) => {
    setEntries(entries.map((e) => e.categoryId === catId ? { ...e, ...updates } : e));
    setResult(null);
  };

  // Union of all selected colors across entries (for Etsy Color dimension)
  const allSelectedColors = useMemo(() => {
    const set = new Set<string>();
    for (const entry of entries) {
      for (const c of entry.selectedColors) set.add(c);
    }
    return Array.from(set);
  }, [entries]);

  // Total size options across all entries
  const totalSizeOptions = entries.reduce((sum, e) => sum + e.selectedSizes.length, 0);
  // Renksiz urunler (ornament): Color ekseni yok, kombinasyon = beden sayisi
  const colorless = entries.length > 0 && entries.every((e) =>
    !categories.find((c: ProductCategory) => c.id === e.categoryId)?.colorAvailability?.length);
  // Actually sellable cells: each category only pairs its own sizes with its own
  // colors (the rest are disabled). This is the number the user cares about.
  // Secili urunlerden en az birinde cift yuz baski maliyeti var mi
  const hasDoubleSide = colorless && entries.some((e) =>
    categories.find((c: ProductCategory) => c.id === e.categoryId)?.sizeOptions
      .some((so) => Object.keys(so.printCosts ?? {}).some((k) => /[çc]ift|arkal[iı]|double|two.?sided|both|\+/i.test(k))));
  const usePrintAxis = hasDoubleSide && printAxisOn;
  const effectiveSizeAxis = sizeAxisName.trim() || (colorless ? "Style" : "Size");
  const totalCombinations = totalSizeOptions * (colorless ? (usePrintAxis ? 2 : 1) : allSelectedColors.length); // full matrix sent to Etsy
  const activeCombinations = colorless
    ? totalSizeOptions * (usePrintAxis ? 2 : 1)
    : entries.reduce((sum, e) => sum + e.selectedSizes.length * e.selectedColors.length, 0);
  const disabledCount = totalCombinations - activeCombinations;
  const COMBO_LIMIT = 400; // Etsy toplam kombinasyon limiti
  const COMBO_WARN = Math.round(COMBO_LIMIT * 0.9); // 360 — uyari esigi
  const isOverLimit = totalCombinations > COMBO_LIMIT;
  const maxColorsForLimit = totalSizeOptions > 0 ? Math.floor(COMBO_LIMIT / totalSizeOptions) : 0;

  const availableCats = categories.filter((c: ProductCategory) => !entries.some((e) => e.categoryId === c.id));

  const isAdded = (catId: string) => entries.some((e) => e.categoryId === catId);

  // Katalogta sabit fiyati olan beden sayisi — "Katalog Fiyati" modunun kapsami
  const { fixedPriceCount, fixedPriceTotal } = useMemo(() => {
    let withPrice = 0, total = 0;
    for (const e of entries) {
      const cat = categories.find((c: ProductCategory) => c.id === e.categoryId);
      if (!cat) continue;
      for (const s of cat.sizeOptions) {
        if (e.selectedSizes.length > 0 && !e.selectedSizes.includes(s.size)) continue;
        total++;
        if (typeof s.retailPrice === "number" && s.retailPrice > 0) withPrice++;
      }
    }
    return { fixedPriceCount: withPrice, fixedPriceTotal: total };
  }, [entries, categories]);

  // Katalogta sabit fiyat tanimliysa varsayilan mod odur — fiyatin tek kaynagi
  // katalog olsun diye. Kullanici modu degistirdiyse dokunma.
  const [pricingTouched, setPricingTouched] = useState(false);
  useEffect(() => {
    if (pricingTouched) return;
    if (fixedPriceCount > 0 && pricingMode !== "catalog_fixed") setPricingMode("catalog_fixed");
  }, [fixedPriceCount, pricingTouched, pricingMode]);

  // Secili TUM kategorilerde bulunan renkler — combo'da asil ise yarayanlar
  // Renk -> o rengi bulunduran kategori adlari. Bir renk kac modelde varsa,
  // Etsy'ye giden renk boyutunda yine TEK renk sayilir; ortakligi gormek
  // kombinasyonu sismeden hangi renklerin secilebilecegini gosterir.
  const colorOwners = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const e of entries) {
      const cat = categories.find((c: ProductCategory) => c.id === e.categoryId);
      if (!cat) continue;
      for (const ca of (cat as ProductCategory).colorAvailability) {
        const list = map.get(ca.color) ?? [];
        list.push((cat as ProductCategory).name);
        map.set(ca.color, list);
      }
    }
    return map;
  }, [entries, categories]);

  const commonColors = useMemo(() => {
    if (entries.length < 2) return new Set<string>();
    const lists = entries
      .map((e) => categories.find((c: ProductCategory) => c.id === e.categoryId))
      .filter(Boolean)
      .map((c) => new Set((c as ProductCategory).colorAvailability.map((ca) => ca.color)));
    if (lists.length < 2) return new Set<string>();
    return new Set([...lists[0]].filter((color) => lists.every((s) => s.has(color))));
  }, [entries, categories]);

  // 2+ modelde var ama hepsinde yok — kismi ortak
  const partialColors = useMemo(() => {
    if (entries.length < 3) return new Set<string>();
    const set = new Set<string>();
    for (const [color, owners] of colorOwners) {
      if (owners.length >= 2 && owners.length < entries.length) set.add(color);
    }
    return set;
  }, [colorOwners, entries.length]);

  // Kategorileri gruplarina gore bol — "Gruplanmamis" en sonda
  const catsByGroup = useMemo(() => {
    const UNGROUPED = "Gruplanmamis";
    const map = new Map<string, ProductCategory[]>();
    for (const c of categories as ProductCategory[]) {
      const key = c.group?.trim() || UNGROUPED;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(c);
    }
    const entriesArr = [...map.entries()]
      .filter(([k]) => k !== UNGROUPED)
      .sort((a, b) => a[0].localeCompare(b[0], "tr"));
    const un = map.get(UNGROUPED);
    if (un?.length) entriesArr.push([UNGROUPED, un]);
    return entriesArr;
  }, [categories]);

  const handleGenerate = async () => {
    if (entries.length < 2) return;
    setGenerating(true);
    setError(null);
    try {
      const pricing: PricingStrategy = {
        mode: pricingMode,
        marginPercent: pricingMode === "margin_percent" ? marginPercent : undefined,
        fixedProfit: pricingMode === "fixed_profit" ? fixedProfit : undefined,
        targetBasePrice: pricingMode === "target_price" ? targetBasePrice : undefined,
        shippingCost,
        otherCosts,
        shippingCharge,
        offsiteAds,
        discountPct,
      };

      const res = await apiClient.generateComboVariations({
        catalogId: catalog.id,
        categories: entries.map((e) => ({
          categoryId: e.categoryId,
          sizePrefix: e.sizePrefix,
          sizeLabels: e.sizeLabels,
          selectedSizes: e.selectedSizes,
          selectedColors: e.selectedColors,
        })),
        pricing,
        sizeAxisName: effectiveSizeAxis,
        ...(usePrintAxis ? { printAxis } : {}),
      });

      setResult(res);
      setDoubleAdjust(null);
      setEditedBreakdown(res.pricingBreakdown.map((p: ComboPricedVariant) => ({ ...p })));
    } catch (err: any) {
      setError(err.message || "Combo varyasyon oluşturma başarısız");
    } finally {
      setGenerating(false);
    }
  };

  // Recalc profit & margin from edited selling price
  const recalcRow = (row: ComboPricedVariant, newSellingPrice: number): ComboPricedVariant => {
    const { net: profit, marginPct: profitMargin } = etsyNetProfit({
      price: discountedPrice(newSellingPrice, discountPct), productCost: row.cost, shippingCost, extraCost: otherCosts, shippingCharge, offsiteAds,
    });
    return {
      ...row,
      sellingPrice: Math.round(newSellingPrice * 100) / 100,
      profit: Math.round(profit * 100) / 100,
      profitMargin: Math.round(profitMargin * 10) / 10,
    };
  };

  const updateRowPrice = (index: number, newPrice: number) => {
    setEditedBreakdown((prev) =>
      prev.map((p, i) => (i === index ? recalcRow(p, newPrice) : p))
    );
  };

  const updateCategoryPrices = (categoryId: string, newPrice: number) => {
    setEditedBreakdown((prev) =>
      prev.map((p) => (p.categoryId === categoryId ? recalcRow(p, newPrice) : p))
    );
  };

  // Compute edited base price & variations from editedBreakdown
  const editedBasePrice = editedBreakdown.length > 0
    ? Math.min(...editedBreakdown.map((p) => p.sellingPrice))
    : 0;

  const editedVariations = useMemo(() => {
    if (editedBreakdown.length === 0 || !result) return result?.variations ?? [];
    const sizeOpts = editedBreakdown.map((p) => ({
      label: p.prefixedLabel,
      priceAdjust: Math.round((p.sellingPrice - editedBasePrice) * 100) / 100,
      cost: p.cost,
      sellingPrice: p.sellingPrice,
    }));
    // 2. eksen (Color ya da baski yuzu) sonuctan aynen; cift yuz farki elle degistirilebilir
    const rest = result.variations.slice(1).map((v) => doubleAdjust == null || v.options.length !== 2 || v.name === "Color" ? v
      : { ...v, options: [v.options[0], { ...v.options[1], priceAdjust: Math.max(0, doubleAdjust) }] });
    return [{ name: result.variations[0]?.name ?? "Size", options: sizeOpts }, ...rest];
  }, [editedBreakdown, editedBasePrice, result, doubleAdjust]);

  const handleCopyToClipboard = () => {
    if (!result) return;
    navigator.clipboard.writeText(JSON.stringify(editedVariations, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSaveAsTemplate = async () => {
    if (!result) return;
    const catNames = entries.map((e) => {
      const cat = categories.find((c: ProductCategory) => c.id === e.categoryId);
      return cat?.name || e.categoryId;
    });
    const defaultName = `Combo: ${catNames.join(" + ")}`;
    const name = prompt("Şablon adı:", defaultName);
    if (!name) return;
    // Kategoriler tek markadan geliyorsa markayi otomatik ata (degilse bos, sonra duzenlenir)
    const comboBrands = Array.from(new Set(entries.map((e) => categories.find((c: ProductCategory) => c.id === e.categoryId)?.brand).filter(Boolean)));
    try {
      await apiClient.createVariationTemplate({
        name,
        brand: comboBrands.length === 1 ? (comboBrands[0] as string) : null,
        category: "combo-catalog",
        variations: editedVariations,
        // 2. eksen Color ya da baski yuzu olabilir: anahtar eksen adlarindan
        disabledCombos: (result.disabledCombinations ?? []).map((dc) => ({
          [editedVariations[0]?.name ?? "Size"]: dc.size,
          [editedVariations[1]?.name ?? "Color"]: dc.color,
        })),
        syncSource: {
          catalogId: catalog.id,
          categories: entries.map((e) => ({ categoryId: e.categoryId, sizePrefix: e.sizePrefix, sizeLabels: Object.keys(e.sizeLabels).length ? e.sizeLabels : null, selectedSizes: e.selectedSizes?.length ? e.selectedSizes : null, selectedColors: e.selectedColors?.length ? e.selectedColors : null })),
        },
      });
      alert("Combo varyasyon şablonu kaydedildi!");
    } catch {
      alert("Kaydetme başarısız");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 pt-10">
      <div className="w-full max-w-5xl rounded-2xl bg-white p-6 shadow-2xl">
        {/* Header */}
        <div className="mb-5 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              <Layers className="mr-2 inline h-5 w-5 text-indigo-600" />
              Combo Varyasyon Oluşturucu
            </h2>
            <p className="text-sm text-gray-500">
              Birden fazla kategoriyi tek Etsy listing'de birleştirin | {catalog.name}
            </p>
          </div>
          <button onClick={onClose} className="rounded-lg p-1 hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="grid gap-6 lg:grid-cols-5">
          {/* Left: Config (3 cols) */}
          <div className="space-y-4 lg:col-span-3">
            {/* Category Entries */}
            <div>
              <label className="mb-2 flex items-center justify-between text-sm font-medium text-gray-700">
                <span>Kategoriler ({entries.length} seçili)</span>
                {availableCats.length > 0 && (
                  <select
                    className="rounded border border-gray-300 px-2 py-1 text-xs"
                    value=""
                    onChange={(e) => { if (e.target.value) addCategory(e.target.value); }}
                  >
                    <option value="">+ Kategori Ekle</option>
                    {catsByGroup.map(([groupName, groupCats]) => (
                      <optgroup key={groupName} label={groupName}>
                        {groupCats.map((cat: ProductCategory) => (
                          <option key={cat.id} value={cat.id} disabled={isAdded(cat.id)}>
                            {isAdded(cat.id) ? "✓ " : ""}{cat.name} ({cat.brand})
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                )}
              </label>

              {/* Hizli secim — eklenenler renkli ve pasif, tekrar eklenemez */}
              <div className="mb-3 rounded-lg border-2 border-dashed border-gray-200 p-3">
                {entries.length < 2 && (
                  <p className="mb-2 text-center text-sm text-gray-500">En az 2 kategori seçin</p>
                )}
                <div className="space-y-2">
                  {catsByGroup.map(([groupName, groupCats]) => {
                    const collapsed = collapsedGroups.has(groupName);
                    const addedCount = groupCats.filter((c: ProductCategory) => isAdded(c.id)).length;
                    return (
                    <div key={groupName}>
                      <button
                        onClick={() => setCollapsedGroups((prev) => {
                          const next = new Set(prev);
                          if (next.has(groupName)) next.delete(groupName); else next.add(groupName);
                          return next;
                        })}
                        className="mb-1 flex w-full items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 hover:text-indigo-500"
                      >
                        {collapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                        {groupName}
                        <span className="font-normal normal-case tracking-normal text-gray-300">
                          ({groupCats.length}{addedCount > 0 ? `, ${addedCount} eklendi` : ""})
                        </span>
                      </button>
                      {!collapsed && (
                      <div className="flex flex-wrap gap-2">
                        {groupCats.map((cat: ProductCategory) => {
                          const added = isAdded(cat.id);
                          return (
                            <button
                              key={cat.id}
                              disabled={added}
                              title={added ? "Zaten eklendi" : "Combo'ya ekle"}
                              onClick={() => addCategory(cat.id)}
                              className={
                                added
                                  ? "cursor-default rounded-lg border border-indigo-300 bg-indigo-100 px-3 py-1.5 text-xs font-medium text-indigo-700"
                                  : "rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-700 hover:border-indigo-300 hover:bg-indigo-50"
                              }
                            >
                              {added ? <Check className="mr-1 inline h-3 w-3" /> : <Plus className="mr-1 inline h-3 w-3" />}
                              {cat.name}
                            </button>
                          );
                        })}
                      </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-2">
                {entries.map((entry) => {
                  const cat = categories.find((c: ProductCategory) => c.id === entry.categoryId);
                  if (!cat) return null;
                  return (
                    <div key={entry.categoryId} className="rounded-lg border border-gray-200 bg-white">
                      <div className="flex items-center justify-between px-3 py-2">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => updateEntry(entry.categoryId, { expanded: !entry.expanded })}
                            className="text-gray-400"
                          >
                            {entry.expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </button>
                          <div>
                            <span className="text-sm font-medium text-gray-900">{cat.name}</span>
                            <span className="ml-1 text-xs text-gray-500">({cat.brand})</span>
                          </div>
                          <span className="rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700">
                            {entry.selectedSizes.length} beden{cat.colorAvailability.length > 0 && `, ${entry.selectedColors.length} renk`}
                          </span>
                        </div>
                        <button
                          onClick={() => removeCategory(entry.categoryId)}
                          className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-500"
                        >
                          <Minus className="h-3.5 w-3.5" />
                        </button>
                      </div>

                      {entry.expanded && (() => {
                        // Per-category color stock info
                        const sizeNames = entry.selectedSizes;
                        const catColors = cat.colorAvailability.map((ca: { color: string; sizes: Record<string, boolean> }) => {
                          const inStock = sizeNames.some((sz: string) => ca.sizes[sz] === true);
                          return { color: ca.color, inStock };
                        }).sort((a: { inStock: boolean }, b: { inStock: boolean }) => (a.inStock === b.inStock ? 0 : a.inStock ? -1 : 1));
                        const inStockColors = catColors.filter((c: { inStock: boolean }) => c.inStock);
                        return (
                        <div className="border-t border-gray-100 px-3 py-2 space-y-2">
                          {/* Size Prefix */}
                          <div>
                            <label className="text-xs text-gray-600">Prefix</label>
                            <input
                              value={entry.sizePrefix}
                              onChange={(e) => updateEntry(entry.categoryId, { sizePrefix: e.target.value })}
                              className="w-full rounded border border-gray-300 px-2 py-1 text-xs"
                              placeholder='örn: "Shirt - "'
                            />
                          </div>
                          {/* Sizes */}
                          <div>
                            <div className="mb-1 flex items-center justify-between">
                              <label className="text-xs text-gray-600">Bedenler</label>
                              <button
                                onClick={() => updateEntry(entry.categoryId, {
                                  selectedSizes: entry.selectedSizes.length === cat.sizeOptions.length
                                    ? [] : cat.sizeOptions.map((s: { size: string }) => s.size)
                                })}
                                className="text-[10px] text-indigo-600 hover:underline"
                              >
                                {entry.selectedSizes.length === cat.sizeOptions.length ? "Temizle" : "Tümünü Seç"}
                              </button>
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {cat.sizeOptions.map((s: { size: string; cost: number }) => {
                                const isOn = entry.selectedSizes.includes(s.size);
                                return (
                                  <button
                                    key={s.size}
                                    onClick={() => updateEntry(entry.categoryId, {
                                      selectedSizes: isOn
                                        ? entry.selectedSizes.filter((sz) => sz !== s.size)
                                        : [...entry.selectedSizes, s.size]
                                    })}
                                    className={`rounded border px-2 py-0.5 text-[10px] ${
                                      isOn ? "border-indigo-300 bg-indigo-50 text-indigo-700" : "border-gray-200 text-gray-400"
                                    }`}
                                  >
                                    {s.size} <span className="text-gray-400">${s.cost.toFixed(2)}</span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                          {/* Etsy'de gorunecek ad: varsayilan prefix + beden, elle degistirilebilir */}
                          {entry.selectedSizes.length > 0 && (
                            <div>
                              <label className="text-xs text-gray-600">Etsy'de görünecek ad</label>
                              <div className="space-y-1">
                                {cat.sizeOptions.filter((s: { size: string }) => entry.selectedSizes.includes(s.size)).map((s: { size: string }) => (
                                  <div key={s.size} className="flex items-center gap-2">
                                    <span className="w-20 shrink-0 truncate text-[10px] text-gray-500" title={s.size}>{s.size}</span>
                                    <input
                                      value={comboSizeLabel(entry, s.size)}
                                      maxLength={45}
                                      aria-label={`${s.size} için Etsy adı`}
                                      onChange={(ev) => {
                                        const next = { ...entry.sizeLabels };
                                        if (ev.target.value === `${entry.sizePrefix}${s.size}`) delete next[s.size];
                                        else next[s.size] = ev.target.value;
                                        updateEntry(entry.categoryId, { sizeLabels: next });
                                      }}
                                      className="min-w-0 flex-1 rounded border border-gray-300 px-2 py-1 text-xs"
                                    />
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {/* Colors per category */}
                          {catColors.length > 0 && (<div>
                            <div className="mb-1 flex items-center justify-between">
                              <label className="text-xs text-gray-600">
                                Renkler <span className="text-gray-400">({entry.selectedColors.length} seçili / {inStockColors.length} stokta / {catColors.length} toplam)</span>
                              </label>
                              <div className="flex gap-1.5">
                                {commonColors.size > 0 && (
                                  <button
                                    title="Seçili kategorilerin hepsinde bulunan renkleri seç"
                                    onClick={() => updateEntry(entry.categoryId, {
                                      selectedColors: catColors
                                        .filter((c: { color: string }) => commonColors.has(c.color))
                                        .map((c: { color: string }) => c.color)
                                    })}
                                    className="text-[10px] text-indigo-600 hover:underline"
                                  >
                                    ◆ Ortak Olanları Seç
                                  </button>
                                )}
                                {partialColors.size > 0 && (
                                  <button
                                    title="En az iki modelde bulunan renkleri seç (hepsinde olmasa da)"
                                    onClick={() => updateEntry(entry.categoryId, {
                                      selectedColors: catColors
                                        .filter((c: { color: string }) => commonColors.has(c.color) || partialColors.has(c.color))
                                        .map((c: { color: string }) => c.color)
                                    })}
                                    className="text-[10px] text-amber-600 hover:underline"
                                  >
                                    ◈ 2+ Ortakları Seç
                                  </button>
                                )}
                                <button
                                  onClick={() => updateEntry(entry.categoryId, {
                                    selectedColors: inStockColors.map((c: { color: string }) => c.color)
                                  })}
                                  className="text-[10px] text-green-600 hover:underline"
                                >
                                  Stokta Olanları Seç
                                </button>
                                <button
                                  onClick={() => updateEntry(entry.categoryId, {
                                    selectedColors: entry.selectedColors.length === catColors.length
                                      ? [] : catColors.map((c: { color: string }) => c.color)
                                  })}
                                  className="text-[10px] text-indigo-600 hover:underline"
                                >
                                  {entry.selectedColors.length === catColors.length ? "Temizle" : "Tümünü Seç"}
                                </button>
                              </div>
                            </div>
                            <ColorPasteMatcher
                              catColors={catColors.map((c: { color: string; inStock: boolean }) => c)}
                              selected={entry.selectedColors}
                              onSelect={(colors) => updateEntry(entry.categoryId, { selectedColors: colors })}
                            />
                            <div className="max-h-28 overflow-y-auto rounded border border-gray-100 bg-gray-50/50 p-1.5">
                              <div className="flex flex-wrap gap-1">
                                {catColors.map((cc: { color: string; inStock: boolean }) => {
                                  const isOn = entry.selectedColors.includes(cc.color);
                                  const isCommon = commonColors.has(cc.color);
                                  const isPartial = !isCommon && partialColors.has(cc.color);
                                  const owners = colorOwners.get(cc.color) ?? [];
                                  return (
                                    <button
                                      key={cc.color}
                                      title={
                                        isCommon
                                          ? `Seçili kategorilerin hepsinde var (${owners.length})`
                                          : isPartial
                                            ? `${owners.length} modelde ortak: ${owners.join(", ")}`
                                            : "Yalnızca bu modelde var"
                                      }
                                      onClick={() => updateEntry(entry.categoryId, {
                                        selectedColors: isOn
                                          ? entry.selectedColors.filter((c) => c !== cc.color)
                                          : [...entry.selectedColors, cc.color]
                                      })}
                                      className={`flex items-center gap-0.5 rounded border px-1.5 py-0.5 text-[10px] transition-colors ${
                                        isCommon ? "ring-1 ring-indigo-400 " : isPartial ? "ring-1 ring-amber-300 " : ""
                                      }${
                                        isOn
                                          ? cc.inStock
                                            ? "border-green-300 bg-green-50 text-green-700"
                                            : "border-red-300 bg-red-50 text-red-600"
                                          : cc.inStock
                                            ? "border-gray-200 text-gray-600"
                                            : "border-gray-200 text-gray-300 line-through"
                                      }`}
                                    >
                                      {isCommon && <span className="text-[9px] text-indigo-500">◆</span>}
                                      {isPartial && <span className="text-[9px] text-amber-500">◈{owners.length}</span>}
                                      {isOn && <Check className="h-2.5 w-2.5" />}
                                      {cc.color}
                                      {!cc.inStock && <span className="text-[8px]">✗</span>}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          </div>)}
                        </div>
                        );
                      })()}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Etsy limit gostergesi (400) */}
            {entries.length >= 2 && (
              <div className={`rounded-lg p-3 ${
                isOverLimit ? "border border-red-300 bg-red-50" : totalCombinations > COMBO_WARN ? "border border-yellow-300 bg-yellow-50" : "border border-gray-200 bg-gray-50"
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {isOverLimit && <AlertTriangle className="h-4 w-4 text-red-500" />}
                    {/* Etsy limiti, devre disi hucreleri de sayar: matrisin TAMAMI
                        (Size x Color) inventory'ye gonderiliyor. Limitle karsilastirilan
                        sayi bu yuzden totalCombinations. */}
                    <span className="text-sm font-medium">
                      <span className={isOverLimit ? "font-bold text-red-600" : "font-bold text-green-600"}>{totalCombinations}</span>
                      <span className="font-normal text-gray-500"> / {COMBO_LIMIT} kombinasyon</span>
                      {disabledCount > 0 && (
                        <span className="font-normal text-gray-400">
                          {" "}· {activeCombinations} satılabilir, {disabledCount} devre dışı
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="h-2 w-32 overflow-hidden rounded-full bg-gray-200">
                    <div
                      className={`h-full rounded-full transition-all ${isOverLimit ? "bg-red-500" : totalCombinations > COMBO_WARN ? "bg-yellow-500" : "bg-green-500"}`}
                      style={{ width: `${Math.min(100, (totalCombinations / COMBO_LIMIT) * 100)}%` }}
                    />
                  </div>
                </div>
                {disabledCount > 0 && (
                  <p className="mt-1 text-[11px] text-gray-500">
                    Etsy limiti devre dışı hücreleri de sayar: {totalSizeOptions} beden x {allSelectedColors.length} renk
                    matrisinin tamamı gönderilir.
                  </p>
                )}
                {isOverLimit && (
                  <p className="mt-1 text-xs text-red-600">
                    Limit aşılıyor! {totalSizeOptions} bedenle en fazla {maxColorsForLimit} renk kullanabilirsiniz
                    (= {totalSizeOptions * maxColorsForLimit} kombinasyon) veya beden sayısını azaltın.
                  </p>
                )}
              </div>
            )}

            {/* Color Summary */}
            {entries.length >= 2 && !colorless && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                <div className="flex items-center gap-3 text-xs">
                  <span className="font-medium text-gray-700">Toplam {allSelectedColors.length} benzersiz renk</span>
                  <span className="text-gray-400">|</span>
                  {entries.map((e) => {
                    const cat = categories.find((c: ProductCategory) => c.id === e.categoryId);
                    return (
                      <span key={e.categoryId} className="text-gray-500">
                        {cat?.name}: <span className="font-medium text-indigo-600">{e.selectedColors.length}</span>
                      </span>
                    );
                  })}
                </div>
                {/* Isaret aciklamasi: ayni renk kac modelde olursa olsun renk
                    boyutunda TEK renk sayilir — ortak renk secmek kombinasyonu
                    buyutmez, yalniz bir modelde olan renk ise matrisi sisirir. */}
                <div className="mt-1.5 flex flex-wrap items-center gap-3 text-[10px] text-gray-500">
                  <span><span className="text-indigo-500">◆</span> tüm modellerde ortak ({commonColors.size})</span>
                  {entries.length >= 3 && (
                    <span><span className="text-amber-500">◈</span> 2+ modelde ortak ({partialColors.size})</span>
                  )}
                  <span>işaretsiz = yalnızca tek modelde</span>
                </div>
              </div>
            )}

            {/* Pricing */}
            {entries.length >= 2 && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <label className="mb-2 flex items-center gap-1 text-sm font-medium text-gray-700">
                  <DollarSign className="h-3.5 w-3.5" />
                  Fiyatlandırma
                </label>
                <div className="mb-3 flex gap-1 rounded-lg bg-gray-200 p-0.5">
                  {([
                    { mode: "catalog_fixed" as PricingMode, label: "Katalog Fiyatı" },
                    { mode: "margin_percent" as PricingMode, label: "% Marj" },
                    { mode: "fixed_profit" as PricingMode, label: "Sabit Kâr" },
                    { mode: "target_price" as PricingMode, label: "Hedef Fiyat" },
                  ]).map((m) => (
                    <button
                      key={m.mode}
                      onClick={() => { setPricingTouched(true); setPricingMode(m.mode); }}
                      className={`flex-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${
                        pricingMode === m.mode ? "bg-white text-gray-900 shadow-sm" : "text-gray-600"
                      }`}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
                <div className="mb-3">
                  {pricingMode === "margin_percent" && (
                    <div>
                      <label className="text-xs text-gray-600">Hedef Kâr Marjı (%)</label>
                      <input type="number" value={marginPercent} onChange={(e) => setMarginPercent(Number(e.target.value))} className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm" min={1} max={90} />
                    </div>
                  )}
                  {pricingMode === "fixed_profit" && (
                    <div>
                      <label className="text-xs text-gray-600">Sabit Kâr ($)</label>
                      <input type="number" value={fixedProfit} onChange={(e) => setFixedProfit(Number(e.target.value))} className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm" min={0} step={0.5} />
                    </div>
                  )}
                  {pricingMode === "target_price" && (
                    <div>
                      <label className="text-xs text-gray-600">Hedef Baz Fiyat ($)</label>
                      <input type="number" value={targetBasePrice} onChange={(e) => setTargetBasePrice(Number(e.target.value))} className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm" min={0} step={0.01} />
                    </div>
                  )}
                  {pricingMode === "catalog_fixed" && (
                    <div className="rounded border border-emerald-200 bg-emerald-50 px-2 py-1.5 text-[11px] text-emerald-800">
                      Fiyatlar katalogdaki sabit satış fiyatından alınır
                      ({fixedPriceCount}/{fixedPriceTotal} beden).
                      {fixedPriceCount < fixedPriceTotal && (
                        <> Fiyatı girilmemiş bedenler aşağıdaki marja göre hesaplanır.</>
                      )}
                      <div className="mt-1 text-emerald-700">
                        Fiyatı değiştirmek için katalogda ilgili ürünün "Fiyatları düzenle" ekranını kullanın.
                      </div>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <EtsyShippingInputs
                    shippingCharge={shippingCharge} setShippingCharge={setShippingCharge}
                    shippingCost={shippingCost} setShippingCost={setShippingCost}
                    offsiteAds={offsiteAds} setOffsiteAds={setOffsiteAds}
                  discountPct={discountPct} setDiscountPct={setDiscountPct}
                  />
                  <div>
                    <label className="text-xs text-gray-600">Diğer ($)</label>
                    <input type="number" value={otherCosts} onChange={(e) => setOtherCosts(Number(e.target.value))} className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm" step={0.5} />
                  </div>
                </div>
              </div>
            )}

            {/* Eksen basliklari: renksiz urunde 1. eksen Style, baski yuzu 2. eksen */}
            {entries.length >= 2 && colorless && (
              <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
                <div>
                  <label className="text-xs text-gray-600">1. eksenin Etsy başlığı</label>
                  <input value={sizeAxisName} onChange={(e) => { setSizeAxisName(e.target.value); setResult(null); }}
                    placeholder="Style" maxLength={45} className="w-full rounded border border-gray-300 px-2 py-1 text-xs" />
                </div>
                {hasDoubleSide ? (
                  <>
                    <label className="flex items-center gap-2 text-xs text-gray-700">
                      <input type="checkbox" checked={printAxisOn} onChange={(e) => { setPrintAxisOn(e.target.checked); setResult(null); }} />
                      Baskı yüzü 2. eksen (alıcı tek/çift yüz seçer)
                    </label>
                    {printAxisOn && (
                      <div className="grid grid-cols-3 gap-2">
                        {([["name", "Başlık"], ["single", "Tek yüz"], ["double", "Çift yüz"]] as const).map(([k, lbl]) => (
                          <div key={k}>
                            <label className="text-[10px] text-gray-500">{lbl}</label>
                            <input value={printAxis[k]} maxLength={45}
                              onChange={(e) => { setPrintAxis({ ...printAxis, [k]: e.target.value }); setResult(null); }}
                              className="w-full rounded border border-gray-300 px-2 py-1 text-xs" />
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-[10px] text-gray-500">Seçili ürünlerde çift yüz baskı fiyatı yok; baskı ekseni için ürünleri eklentiden yeniden çekin.</p>
                )}
              </div>
            )}

            {/* Generate */}
            <button
              onClick={handleGenerate}
              disabled={generating || entries.length < 2 || (!colorless && allSelectedColors.length === 0) || isOverLimit}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calculator className="h-4 w-4" />}
              Combo Hesapla ({activeCombinations} satılabilir kombinasyon)
            </button>
            {error && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</div>}
          </div>

          {/* Right: Results (2 cols) */}
          <div className="lg:col-span-2">
            {result ? (
              <div className="space-y-4">
                {/* Summary */}
                <div className={`rounded-lg p-3 ${result.limitWarning ? "border border-yellow-300 bg-yellow-50" : "border border-green-200 bg-green-50"}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-green-800">
                        {result.totalCombinations - (result.disabledCombinations?.length ?? 0)} aktif varyasyon
                        {(result.disabledCombinations?.length ?? 0) > 0 && (
                          <span className="font-normal text-green-600"> ({result.totalCombinations} hücre)</span>
                        )}
                      </p>
                      <p className="text-xs text-green-600">Baz: ${editedBasePrice.toFixed(2)}</p>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={handleCopyToClipboard} className="flex items-center gap-1 rounded-lg bg-white px-2 py-1 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50">
                        {copied ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
                        {copied ? "OK" : "JSON"}
                      </button>
                      <button onClick={handleSaveAsTemplate} className="flex items-center gap-1 rounded-lg bg-indigo-600 px-2 py-1 text-xs font-medium text-white hover:bg-indigo-700">
                        Kaydet
                      </button>
                    </div>
                  </div>
                  {result.limitWarning && (
                    <p className="mt-1 flex items-center gap-1 text-xs text-yellow-700">
                      <AlertTriangle className="h-3 w-3" />{result.limitWarning}
                    </p>
                  )}
                </div>

                {/* Editable Price Table - grouped by category */}
                <div>
                  <p className="mb-2 text-sm font-medium text-gray-700">Fiyat Tablosu <span className="text-[10px] font-normal text-gray-400">(satış fiyatını tıklayarak düzenleyin)</span></p>
                  <div className="overflow-hidden rounded-lg border border-gray-200">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="px-2 py-1.5 text-left font-medium text-gray-600">Beden</th>
                          <th className="px-2 py-1.5 text-right font-medium text-gray-600">Maliyet</th>
                          <th className="px-2 py-1.5 text-right font-medium text-gray-600">Liste $</th>
                          <th className="px-2 py-1.5 text-right font-medium text-gray-600">İndirimli</th>
                          {shippingCharge > 0 && <th className="px-2 py-1.5 text-right font-medium text-gray-600" title="Alıcının ödediği: indirimli satış + kargo">Satış + kargo</th>}
                          <th className="px-2 py-1.5 text-right font-medium text-gray-600">Kâr</th>
                          <th className="px-2 py-1.5 text-right font-medium text-gray-600">%</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          let lastCatId = "";
                          return editedBreakdown.map((p: ComboPricedVariant, i: number) => {
                            const showHeader = p.categoryId !== lastCatId;
                            lastCatId = p.categoryId;
                            return (
                              <React.Fragment key={i}>
                                {showHeader && (
                                  <tr className="bg-indigo-50">
                                    <td colSpan={3} className="px-2 py-1 text-[10px] font-semibold text-indigo-700 uppercase">
                                      {p.categoryName} ({p.brand})
                                    </td>
                                    <td colSpan={shippingCharge > 0 ? 4 : 3} className="px-2 py-1 text-right">
                                      <div className="flex items-center justify-end gap-1">
                                        <span className="text-[10px] text-indigo-500">Toplu $</span>
                                        <input
                                          type="number"
                                          className="w-16 rounded border border-indigo-200 bg-white px-1.5 py-0.5 text-right text-[10px] text-indigo-700"
                                          placeholder="0.00"
                                          step={0.01}
                                          min={0}
                                          value={bulkPrice[p.categoryId] ?? ""}
                                          onChange={(e) => setBulkPrice((prev) => ({ ...prev, [p.categoryId]: e.target.value }))}
                                          onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                              const val = parseFloat(bulkPrice[p.categoryId] || "0");
                                              if (val > 0) updateCategoryPrices(p.categoryId, val);
                                            }
                                          }}
                                        />
                                        <button
                                          onClick={() => {
                                            const val = parseFloat(bulkPrice[p.categoryId] || "0");
                                            if (val > 0) updateCategoryPrices(p.categoryId, val);
                                          }}
                                          className="rounded bg-indigo-600 px-1.5 py-0.5 text-[10px] text-white hover:bg-indigo-700"
                                        >
                                          Uygula
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                                <tr className={i % 2 === 0 ? "" : "bg-gray-50/50"}>
                                  <td className="px-2 py-1 font-medium text-gray-800">{p.prefixedLabel}</td>
                                  <td className="px-2 py-1 text-right text-gray-500">${p.cost.toFixed(2)}</td>
                                  <td className="px-2 py-1 text-right">
                                    <input
                                      type="number"
                                      className="w-16 rounded border border-gray-200 px-1 py-0.5 text-right text-xs font-medium focus:border-indigo-400 focus:outline-none"
                                      value={p.sellingPrice}
                                      min={0}
                                      step={0.01}
                                      onChange={(e) => updateRowPrice(i, parseFloat(e.target.value) || 0)}
                                    />
                                  </td>
                                  <td className="px-2 py-1 text-right text-gray-500">${discountedPrice(p.sellingPrice, discountPct).toFixed(2)}</td>
                                  {shippingCharge > 0 && <td className="whitespace-nowrap px-2 py-1 text-right text-gray-500">${discountedPrice(p.sellingPrice, discountPct).toFixed(2)} + ${shippingCharge.toFixed(2)}</td>}
                                  <td className={`px-2 py-1 text-right ${p.profit >= 0 ? "text-green-600" : "text-red-600"}`}>${p.profit.toFixed(2)}</td>
                                  <td className="px-2 py-1 text-right">
                                    <span className={`rounded px-1 py-0.5 text-[10px] ${
                                      p.profitMargin >= 30 ? "bg-green-100 text-green-700" : p.profitMargin >= 20 ? "bg-yellow-100 text-yellow-700" : "bg-red-100 text-red-700"
                                    }`}>{p.profitMargin.toFixed(0)}%</span>
                                  </td>
                                </tr>
                              </React.Fragment>
                            );
                          });
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Cift yuz farki: tek tutar, urun bazli farklarin en buyugu */}
                {(() => {
                  const pv = editedVariations[1];
                  if (!pv || pv.name === "Color" || pv.options.length !== 2) return null;
                  const rows = editedBreakdown.filter((p) => p.doubleSellingPrice != null);
                  return (
                    <div className="rounded-lg border border-gray-200 p-3 text-xs">
                      <label className="flex items-center gap-2 text-gray-700">
                        {pv.options[1].label} farkı (+$)
                        <input type="number" step={0.5} min={0} value={pv.options[1].priceAdjust ?? 0}
                          onChange={(e) => setDoubleAdjust(Number(e.target.value))}
                          className="w-20 rounded border border-gray-300 px-2 py-1" />
                      </label>
                      <p className="mt-1 text-[10px] text-gray-500">
                        Tüm ürünlere aynı fark eklenir. Ürün bazlı fark: {rows.map((p) => `${p.prefixedLabel} +$${(p.doubleSellingPrice! - p.sellingPrice).toFixed(2)}`).join(" · ") || "—"}
                      </p>
                    </div>
                  );
                })()}

                {/* Variation Preview */}
                <div>
                  <p className="mb-2 text-sm font-medium text-gray-700">Etsy Önizleme</p>
                  {editedVariations.map((v: { name: string; options: { label: string; priceAdjust?: number }[] }, vi: number) => (
                    <div key={vi} className="mb-2">
                      <p className="mb-1 text-xs font-semibold text-gray-500 uppercase">{v.name} ({v.options.length})</p>
                      <div className="flex flex-wrap gap-1">
                        {v.options.map((opt: { label: string; priceAdjust?: number }, oi: number) => (
                          <span key={oi} className="rounded border border-gray-200 bg-white px-1.5 py-0.5 text-[10px] text-gray-700">
                            {opt.label}{opt.priceAdjust ? ` (+$${opt.priceAdjust.toFixed(2)})` : ""}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {(result.disabledCombinations?.length ?? 0) > 0 && (
                  <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
                    <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                    <p>
                      <strong>{result.disabledCombinations!.length} geçersiz kombinasyon</strong> devre dışı bırakılacak
                      {editedVariations[1]?.name === "Color"
                        ? <>(bir bedenin kategorisi o rengi taşımadığı için). Etsy'de bu hücreler kapalı (is_enabled=false) gönderilir — örn. Youth bedenleri yalnızca kendi renkleriyle satılır.</>
                        : <>(çift yüz baskısı olmayan ürünler). Etsy'de bu hücreler kapalı (is_enabled=false) gönderilir.</>}
                    </p>
                  </div>
                )}

                <div className="flex items-start gap-2 rounded-lg bg-blue-50 p-3 text-xs text-blue-700">
                  <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                  <p>Etsy'de "Prices vary for each Size and Color" seçin. Size boyutu farklı ürün tiplerini prefix ile ayırır.</p>
                </div>
              </div>
            ) : (
              <div className="flex h-full items-center justify-center rounded-lg border-2 border-dashed border-gray-200 p-8">
                <div className="text-center">
                  <Layers className="mx-auto h-10 w-10 text-gray-300" />
                  <p className="mt-2 text-sm text-gray-500">En az 2 kategori seçin, renkleri belirleyin ve "Combo Hesapla" basın</p>
                  <p className="mt-1 text-xs text-gray-400">Örnek: Shirt + Sweatshirt = tek liste</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


/** Bir urunun URETILMIS gorselleri (ProductShot). Studio'da ya da Listing
 *  Builder'da uretilenler burada birikir: ana gorsel secilir, gereksizler
 *  silinir. Ayni sahneyi ikinci kez uretmeye gerek kalmaz. */
/** Takinin GERCEK olcusu. Konsept fotograflarinda boyut tutarliligi icin
 *  modele vucut oraniyla birlikte verilir. Bos ise aciklamadan tahmin edilir. */
function PhysicalSizeEditor({ catalogId, cat }: { catalogId: string; cat: ProductCategory }) {
  const qc = useQueryClient();
  const stored = cat.physicalSize ?? {};
  const [w, setW] = useState(stored.widthMm ? String(stored.widthMm) : "");
  const [h, setH] = useState(stored.heightMm ? String(stored.heightMm) : "");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  // Aciklamadan tahmin (sunucudaki extractSizeMm ile ayni kaba kural)
  const guess = (() => {
    const t = [cat.name, cat.source?.descriptionText ?? "", ...Object.entries(cat.source?.attributes ?? {}).map(([k, v]) => `${k}: ${v}`)].join(" ").replace(/,/g, ".");
    const pair = t.match(/(\d+(?:\.\d+)?)\s*[x×*]\s*(\d+(?:\.\d+)?)\s*(mm|cm)\b/i);
    if (pair) { const k = pair[3].toLowerCase() === "cm" ? 10 : 1; return `${Number(pair[1]) * k} x ${Number(pair[2]) * k} mm`; }
    for (const m of t.matchAll(/(\d+(?:\.\d+)?)\s*(mm|cm)\b/gi)) {
      const v = Number(m[1]) * (m[2].toLowerCase() === "cm" ? 10 : 1);
      if (v >= 3 && v <= 80) return `${v} mm`;
    }
    return null;
  })();
  async function save() {
    setBusy(true); setMsg(null);
    try {
      const r = await fetch(`${getBackendUrl()}/api/supplier-catalogs/${catalogId}/categories/${cat.id}/physical-size`, {
        method: "PATCH",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ widthMm: w ? Number(w.replace(",", ".")) : null, heightMm: h ? Number(h.replace(",", ".")) : null }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || "Kaydedilemedi");
      setMsg("Kaydedildi");
      qc.invalidateQueries({ queryKey: ["supplier-catalogs"] });
    } catch (e) { setMsg(e instanceof Error ? e.message : "Kaydedilemedi"); }
    finally { setBusy(false); }
  }
  return (
    <div onClick={(e) => e.stopPropagation()} className="space-y-1">
      <div className="flex items-center gap-1 text-xs">
        <input value={w} onChange={(e) => setW(e.target.value)} placeholder="Genişlik" className="w-20 rounded border border-gray-200 px-2 py-1" />
        <span className="text-gray-400">x</span>
        <input value={h} onChange={(e) => setH(e.target.value)} placeholder="Yükseklik" className="w-20 rounded border border-gray-200 px-2 py-1" />
        <span className="text-gray-400">mm</span>
        <button onClick={() => void save()} disabled={busy}
          className="rounded bg-indigo-600 px-2 py-1 text-[11px] font-medium text-white hover:bg-indigo-700 disabled:opacity-50">Kaydet</button>
      </div>
      <p className="text-[10px] text-gray-400">
        Konsept fotoğraflarında boyut tutarlılığı için modele verilir.
        {stored.estimated && " AI'nin ürün fotoğrafından tahmini; gerçek ölçüyle düzeltip kaydedin."}
        {!stored.widthMm && !stored.heightMm && (guess ? ` Boş: açıklamadan ${guess} kullanılır.` : " Boş ve açıklamada ölçü yok: ilk üretimde fotoğraftan bir kez tahmin edilip kaydedilir.")}
      </p>
      {msg && <p className="text-[10px] text-gray-500">{msg}</p>}
    </div>
  );
}

/** Urune ozel kargo maliyeti (USD). Bos: varsayilan kargo. Maliyete eklenir;
 *  satis fiyati (maliyet + kargo) x carpan ile hesaplanir. */
function ShippingCostEditor({ catalogId, cat }: { catalogId: string; cat: ProductCategory }) {
  const qc = useQueryClient();
  const stored = cat.shippingCostOverride;
  const [v, setV] = useState(stored != null ? String(stored) : "");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  async function save() {
    setBusy(true); setMsg(null);
    try {
      const r = await fetch(`${getBackendUrl()}/api/supplier-catalogs/${catalogId}/categories/${cat.id}/shipping-cost`, {
        method: "PATCH",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ usd: v.trim() ? Number(v.replace(",", ".")) : null }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || "Kaydedilemedi");
      setMsg("Kaydedildi");
      qc.invalidateQueries({ queryKey: ["supplier-catalogs"] });
    } catch (e) { setMsg(e instanceof Error ? e.message : "Kaydedilemedi"); }
    finally { setBusy(false); }
  }
  return (
    <div onClick={(e) => e.stopPropagation()} className="space-y-1">
      <div className="flex items-center gap-1 text-xs">
        <input value={v} onChange={(e) => setV(e.target.value)} placeholder={String(DEFAULT_ETSY_SHIPPING_USD)}
          className="w-24 rounded border border-gray-200 px-2 py-1" />
        <span className="text-gray-400">USD</span>
        <button onClick={() => void save()} disabled={busy}
          className="rounded bg-indigo-600 px-2 py-1 text-[11px] font-medium text-white hover:bg-indigo-700 disabled:opacity-50">Kaydet</button>
      </div>
      <p className="text-[10px] text-gray-400">
        Maliyete eklenir; fiyat (maliyet + kargo) × çarpan.
        {stored == null && ` Boş: varsayılan ${DEFAULT_ETSY_SHIPPING_USD} USD.`}
      </p>
      {msg && <p className="text-[10px] text-gray-500">{msg}</p>}
    </div>
  );
}

/** Urunun ilham listeleri: Etsy baglantisi eklenir, baslik ve etiketler
 *  sunucuda Etsy API'den cekilir. Liste icerigi uretilirken AI bunlari ornek alir. */
function InspirationsEditor({ catalogId, cat }: { catalogId: string; cat: ProductCategory }) {
  const qc = useQueryClient();
  const list = cat.inspirations ?? [];
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const base = `${getBackendUrl()}/api/supplier-catalogs/${catalogId}/categories/${cat.id}/inspirations`;
  async function call(path: string, init: RequestInit) {
    setBusy(true); setErr(null);
    try {
      const r = await fetch(path, { ...init, headers: { ...authHeaders(), "Content-Type": "application/json" } });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || "İşlem başarısız");
      await qc.invalidateQueries({ queryKey: ["supplier-catalogs"] });
      return true;
    } catch (e) { setErr(e instanceof Error ? e.message : "İşlem başarısız"); return false; }
    finally { setBusy(false); }
  }
  return (
    <div onClick={(e) => e.stopPropagation()} className="space-y-2">
      <div className="flex items-center gap-1 text-xs">
        <input value={url} onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && url.trim()) void call(base, { method: "POST", body: JSON.stringify({ url }) }).then((ok) => ok && setUrl("")); }}
          placeholder="https://www.etsy.com/listing/…"
          className="min-w-0 flex-1 rounded border border-gray-200 px-2 py-1" />
        <button onClick={() => void call(base, { method: "POST", body: JSON.stringify({ url }) }).then((ok) => ok && setUrl(""))}
          disabled={busy || !url.trim()}
          className="flex items-center gap-1 rounded bg-indigo-600 px-2 py-1 text-[11px] font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : null} Ekle
        </button>
      </div>
      {err && <p className="text-[10px] text-red-600">{err}</p>}
      {list.length === 0 ? (
        <p className="text-[10px] text-gray-400">Benzer, iyi satan Etsy listelerini ekleyin — başlık ve etiketleri çekilir, liste başlığı ve etiketleri üretilirken örnek alınır.</p>
      ) : (
        <ul className="space-y-1.5">
          {list.map((x) => (
            <li key={x.listingId} className="rounded border border-gray-200 bg-gray-50 p-2 text-[11px]">
              <div className="flex items-start justify-between gap-2">
                <a href={x.url} target="_blank" rel="noreferrer" className="font-medium text-gray-800 hover:text-indigo-600 hover:underline">
                  {x.title || `Liste ${x.listingId}`} ↗
                </a>
                <button onClick={() => void call(`${base}/${x.listingId}`, { method: "DELETE" })} disabled={busy}
                  title="Kaldır" className="shrink-0 text-gray-400 hover:text-red-600 disabled:opacity-50">✕</button>
              </div>
              {(x.images?.length ?? 0) > 0 && (
                <a href={x.url} target="_blank" rel="noreferrer" className="mt-1 flex gap-1 overflow-x-auto" title="Etsy ilanını aç">
                  {x.images!.slice(0, 8).map((u) => <img key={u} src={u} alt="" loading="lazy" className="h-14 w-14 shrink-0 rounded-lg border border-gray-200 object-cover" />)}
                </a>
              )}
              {x.taxonomyId
                ? <p className="mt-0.5 text-[10px] text-indigo-700">Kategori: {x.taxonomyPath ?? `#${x.taxonomyId} (adı alınamadı — Etsy'den yenile)`}</p>
                : x.taxonomyId === null && <p className="mt-0.5 text-[10px] text-amber-600">Kategori alınamadı — Etsy'den yenile</p>}
              {x.tags.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {x.tags.map((t) => <span key={t} className="rounded-full border border-gray-200 bg-white px-1.5 py-0.5 text-[10px] text-gray-600">{t}</span>)}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {list.length > 0 && (
        <button onClick={() => void call(`${base}/refresh`, { method: "POST" })} disabled={busy}
          className="text-[10px] text-indigo-600 hover:underline disabled:opacity-50">
          Etsy'den yenile (başlık, etiket, kategori, fotoğraf)
        </button>
      )}
    </div>
  );
}

/** Taki turu secici.
 *
 *  Tur, Studio'da hangi konsept sahnelerinin gosterilecegini belirliyor.
 *  Ad uzerinden tahmin cogu zaman dogru ama "Earring" icinde "ring" gecmesi
 *  gibi tuzaklar var; burada ELLE duzeltilir ve tahminin onune gecer. */
function ProductTypePicker({ catalogId, cat }: { catalogId: string; cat: ProductCategory }) {
  const qc = useQueryClient();
  const stored = (cat as unknown as { productTypeKey?: string }).productTypeKey ?? "";
  const [value, setValue] = useState(stored);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const guessed = guessJewelryType(cat.name);
  // Konsept katalogu ile AYNI liste — iki tarafta farkli anahtar kalmasin
  const types = useJewelryTypes();

  useEffect(() => { setValue(stored); }, [stored]);

  async function save(next: string) {
    setValue(next);
    setBusy(true);
    setErr(null);
    try {
      const r = await fetch(`${getBackendUrl()}/api/supplier-catalogs/${catalogId}/categories/${cat.id}/product-type`, {
        method: "PATCH",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ productTypeKey: next }),
      });
      const b = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(b.error || "Kaydedilemedi");
      qc.invalidateQueries({ queryKey: ["supplier-catalogs"] });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Kaydedilemedi");
      setValue(stored);
    } finally { setBusy(false); }
  }

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <select
        value={value}
        disabled={busy}
        onChange={(e) => void save(e.target.value)}
        className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs text-gray-700 disabled:opacity-50"
      >
        <option value="">
          {guessed
            ? `Otomatik: ${types.find((t) => t.key === guessed)?.label ?? JEWELRY_TYPE_LABELS[guessed] ?? guessed}`
            : "Otomatik (tespit edilemedi)"}
        </option>
        {types.map((t) => (
          <option key={t.key} value={t.key}>{t.label}</option>
        ))}
      </select>
      <p className="mt-1 text-[10px] text-gray-400">
        Studio bu türe ait konsept sahnelerini gösterir. Boş bırakılırsa ürün adından tahmin edilir.
      </p>
      {err && <p className="mt-1 text-[10px] text-red-600">{err}</p>}
    </div>
  );
}

function ProductShotStrip({ catalogId, categoryId, onOpenLightbox, onCloseLightbox }: {
  catalogId: string;
  categoryId: string;
  onOpenLightbox: (urls: string[], index: number, onDelete?: (i: number) => void) => void;
  onCloseLightbox: () => void;
}) {
  type Shot = { id: string; imageUrl: string; thumbnailUrl?: string | null; isPrimary: boolean };
  const [shots, setShots] = useState<Shot[]>([]);
  // Tam ekranda arka arkaya silme: kapanis fonksiyonu eski listeyi gormesin
  const shotsRef = useRef<Shot[]>([]);
  useEffect(() => { shotsRef.current = shots; }, [shots]);
  const [busy, setBusy] = useState(false);
  // Bozuk gorseli yeniden uretme penceresi (secilen gorsel)
  const [fixId, setFixId] = useState<string | null>(null);

  const abs = (u: string) => (u.startsWith("http") ? u : `${getBackendUrl()}${u}`);

  async function load() {
    try {
      const r = await fetch(`${getBackendUrl()}/api/product-shots?catalogId=${catalogId}&categoryId=${categoryId}`, { headers: authHeaders() });
      const b = await r.json().catch(() => ({}));
      if (r.ok) setShots((b.data ?? []) as Shot[]);
    } catch { /* galeri alinamadi */ }
  }
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [catalogId, categoryId]);

  async function setPrimary(id: string) {
    setBusy(true);
    try {
      await fetch(`${getBackendUrl()}/api/product-shots/${id}`, {
        method: "PATCH", headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ isPrimary: true }),
      });
      await load();
    } finally { setBusy(false); }
  }

  async function remove(id: string) {
    setBusy(true);
    try {
      await fetch(`${getBackendUrl()}/api/product-shots/${id}`, { method: "DELETE", headers: authHeaders() });
      await load();
    } finally { setBusy(false); }
  }

  /** Tam ekran ac — hatali kare orada da silinebilsin */
  function openFull(i: number) {
    onOpenLightbox(shotsRef.current.map((x) => abs(x.imageUrl)), i, (idx) => void removeFromFull(idx));
  }

  async function removeFromFull(idx: number) {
    const shot = shotsRef.current[idx];
    if (!shot || !window.confirm("Bu görsel silinsin mi?")) return;
    await remove(shot.id);
    const next = shotsRef.current.filter((x) => x.id !== shot.id);
    shotsRef.current = next;
    if (next.length === 0) onCloseLightbox();
    else openFull(Math.min(idx, next.length - 1));
  }

  if (shots.length === 0) return null;
  return (
    <div className="mb-2" onClick={(e) => e.stopPropagation()}>
      <p className="mb-1 flex items-center gap-1 text-[10px] font-medium text-pink-600">
        <Sparkles className="h-3 w-3" /> Üretilen görseller ({shots.length})
      </p>
      <div className="flex gap-1 overflow-x-auto">
        {shots.map((sh, i) => (
          <div key={sh.id} className={`group relative h-16 w-16 shrink-0 overflow-hidden rounded border ${sh.isPrimary ? "border-amber-400 ring-2 ring-amber-100" : "border-gray-200"}`}>
            <img src={abs(sh.thumbnailUrl || sh.imageUrl)} alt="" loading="lazy"
              onClick={() => openFull(i)}
              className="h-full w-full cursor-zoom-in object-cover" />
            {sh.isPrimary && <Star className="absolute left-0.5 top-0.5 h-3 w-3 fill-amber-400 text-amber-400" />}
            <div className="absolute inset-x-0 bottom-0 hidden justify-center gap-1 bg-black/50 py-0.5 group-hover:flex">
              {!sh.isPrimary && (
                <button disabled={busy} onClick={() => setPrimary(sh.id)} title="Ana görsel yap" className="text-white disabled:opacity-50">
                  <Star className="h-3 w-3" />
                </button>
              )}
              <button disabled={busy} onClick={() => setFixId(sh.id)}
                title="Bozuk: yeniden üret ve her yerde güncelle" aria-label="Bozuk: yeniden üret" className="text-white disabled:opacity-50">
                <RefreshCw className="h-3 w-3" />
              </button>
              <button disabled={busy}
                onClick={() => { if (window.confirm("Bu görsel silinsin mi?")) void remove(sh.id); }}
                title="Sil" className="text-white disabled:opacity-50">
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          </div>
        ))}
      </div>
      {fixId && <ShotFixModal shotId={fixId} onClose={() => setFixId(null)} onApplied={() => void load()} />}
    </div>
  );
}


/** Excel'den yapistirilan renk listesiyle karsilastirma.
 *
 *  Renk listeleri elle tutuluyor ve isimler birebir ayni yazilmiyor
 *  ("Blue Jean" / "bluejean", "True Navy" / "Navy"). Karsilastirma
 *  normallestirilmis adla yapilir: kucuk harf, bosluk ve noktalama atilir.
 *  Sonuc uc gruba ayrilir — eslesen, listede olup katalogda olmayan, katalogda
 *  olup listede olmayan. Secim TEK tikla listeye gore kurulabilir. */
function ColorPasteMatcher({ catColors, selected, onSelect }: {
  catColors: Array<{ color: string; inStock: boolean }>;
  selected: string[];
  onSelect: (colors: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");

  const norm = (v: string) => v.toLowerCase().replace(/[^a-z0-9]/g, "");

  // Excel'den gelen metin: satir, sekme, virgul veya noktali virgulle ayrilir
  const pasted = useMemo(
    () => Array.from(new Set(
      text.split(/[\n\r\t,;]+/).map((x) => x.trim()).filter(Boolean),
    )),
    [text],
  );

  const match = useMemo(() => {
    const byNorm = new Map(catColors.map((c) => [norm(c.color), c]));
    const matched: string[] = [];
    const missing: string[] = []; // listede var, katalogda yok
    for (const p of pasted) {
      const hit = byNorm.get(norm(p));
      if (hit) matched.push(hit.color); else missing.push(p);
    }
    const pastedNorms = new Set(pasted.map(norm));
    const extra = catColors.filter((c) => !pastedNorms.has(norm(c.color))).map((c) => c.color);
    return { matched, missing, extra };
  }, [pasted, catColors]);

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="mb-1 text-[10px] text-indigo-600 hover:underline"
        title="Excel'deki renk listesini yapıştır, eşleşenleri seç"
      >
        ⎘ Listeden karşılaştır
      </button>
    );
  }

  return (
    <div className="mb-1.5 rounded-lg border border-indigo-200 bg-indigo-50/40 p-2">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-[10px] font-semibold text-indigo-700">Excel listesiyle karşılaştır</span>
        <button onClick={() => setOpen(false)} className="text-[10px] text-gray-500 hover:text-gray-700">Kapat</button>
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={3}
        placeholder="Excel'den renk sütununu kopyalayıp buraya yapıştır (her satır bir renk)"
        className="w-full rounded border border-gray-200 px-2 py-1 text-[11px]"
      />

      {pasted.length > 0 && (
        <>
          <div className="mt-1.5 flex flex-wrap gap-2 text-[10px]">
            <span className="text-green-700">{match.matched.length} eşleşti</span>
            {match.missing.length > 0 && (
              <span className="text-red-600" title={match.missing.join(", ")}>
                {match.missing.length} listede var, katalogda yok
              </span>
            )}
            {match.extra.length > 0 && (
              <span className="text-amber-600" title={match.extra.join(", ")}>
                {match.extra.length} katalogda var, listede yok
              </span>
            )}
          </div>

          {match.missing.length > 0 && (
            <p className="mt-1 text-[10px] text-gray-500">
              Bulunamayanlar: <span className="text-red-600">{match.missing.join(", ")}</span>
            </p>
          )}

          <div className="mt-1.5 flex flex-wrap gap-1.5">
            <button
              onClick={() => onSelect(match.matched)}
              disabled={match.matched.length === 0}
              className="rounded bg-indigo-600 px-2 py-1 text-[10px] font-medium text-white hover:bg-indigo-700 disabled:opacity-40"
            >
              Eşleşenleri seç ({match.matched.length})
            </button>
            <button
              onClick={() => onSelect(Array.from(new Set([...selected, ...match.matched])))}
              disabled={match.matched.length === 0}
              className="rounded border border-indigo-300 px-2 py-1 text-[10px] font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-40"
            >
              Seçime ekle
            </button>
            <button
              onClick={() => onSelect(selected.filter((c) => match.matched.includes(c)))}
              disabled={selected.length === 0}
              className="rounded border border-gray-300 px-2 py-1 text-[10px] text-gray-600 hover:bg-gray-50 disabled:opacity-40"
            >
              Listede olmayanı çıkar
            </button>
          </div>
        </>
      )}
    </div>
  );
}
